import React, { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { url } from '../../api';
import { apiClient } from '../../api/client';
import { RECAPTCHA_SITE_KEY } from '../../config/env';
import { toast } from 'react-toastify';
import './AddCoffeeForm.css';
import { getAuthToken } from '../../utils/authTokens';
import { fetchIcon } from '../../utils/icons';
import { Dialog, DialogContent, DialogHeader, DialogTitle, Input, Button, Alert, AlertDescription, Checkbox } from '../../ui';
import { MobileSheet } from '../../ui/components/MobileSheet';

// import new status management components
import { useCoffeeSubmission } from './useCoffeeSubmission';
import { useCoffeeDraft, CoffeeDraft, DEFAULT_FORM_DATA, base64ToFile } from './useCoffeeDraft';
import CoffeeStatusIndicator from './CoffeeStatusIndicator';

// structured subcomponents
import StepIndicator from './AddCoffeeForm/components/StepIndicator';
import ImageUploadSection from './AddCoffeeForm/components/ImageUploadSection';
import { CoffeeRatingSection } from '../coffee_rating';
import DescriptionInput from './AddCoffeeForm/components/DescriptionInput';
import { VoiceRecorder } from '../agent_chat/VoiceRecorder';
import StepNavigation from './AddCoffeeForm/components/StepNavigation';
import CoffeeTypeSelector from './AddCoffeeForm/components/CoffeeTypeSelector';
import FormatOptions from './AddCoffeeForm/components/FormatOptions';
import LocationControls from './AddCoffeeForm/components/LocationControls';
import ResumeDraftDialog from './AddCoffeeForm/components/ResumeDraftDialog';
import CoffeeShopList from './AddCoffeeForm/components/CoffeeShopList';
import AddCoffeeShopDialog from './AddCoffeeForm/components/AddCoffeeShopDialog';
import SubmissionError from './AddCoffeeForm/components/SubmissionError';
import HomeBrewFields, {
    HomeBrewState,
    EMPTY_HOME_BREW,
    brewStateFromMachine,
    buildHomeBrew,
} from './AddCoffeeForm/components/HomeBrewFields';
import BrandPicker, { BrandPickerValue } from './AddCoffeeForm/components/BrandPicker';
import TypePicker from './AddCoffeeForm/components/TypePicker';
import ModeSelector from './AddCoffeeForm/components/ModeSelector';
import { supermarketRateCategories, supermarketCoffeeFormat } from './AddCoffeeForm/supermarketCriteria';
import { listMyMachines, listMyBeans, listMyCapsules, submitSupermarketRating, SupermarketRatingPayload } from './api/coffeeApi';
import { CoffeeMachine, CoffeeBean, CoffeeCapsule, SupermarketType } from './CoffeeCardWeb/types';
import { coffeeRatingPaths, coffeeTypePaths, coffeeShopPaths } from './api/paths';
import { searchGoogleShops, proposeGoogleCandidate } from './AddCoffeeForm/shopSearch';
import { haversineKm } from '../coffee_shops/hooks/useUserLocation';

// Note: Coffee format definitions now fetched from backend API

// per-mount cache for coffee type format definitions; refreshed on every form open
// so admin-edited types appear without a hard reload (no cache-once-per-session guard)
let coffeeTypesCache: Record<string, any> = {};
let coffeeTypesFetched = false;

const fetchCoffeeTypesData = async (): Promise<void> => {
    try {
        const response = await apiClient.get(`${url}${coffeeTypePaths.list()}`);
        if (response.data.success) {
            coffeeTypesCache = {};
            response.data.data.forEach((coffee: any) => {
                coffeeTypesCache[coffee.coffeeKey] = coffee;
            });
            coffeeTypesFetched = true;
        }
    } catch (error) {
        console.error('Failed to fetch coffee types:', error);
    }
};

// // import coffee type icons
// import frappeSketo from "../coffee_card/assets/coffees/frappe-sketo.svg";
// import frappeGala from "../coffee_card/assets/coffees/frappe-gala.svg";
// import espresso from "../coffee_card/assets/coffees/espresso.svg";
// import freddoEsp from "../coffee_card/assets/coffees/freddo-esp.svg";
// import freddoCap from "../coffee_card/assets/coffees/freddo-cap.svg";
// import greek from "../coffee_card/assets/coffees/greek-coffee.svg";
// import filter from "../coffee_card/assets/coffees/filter.svg";
// import latte from "../coffee_card/assets/coffees/latte.svg";
// import icedLatte from "../coffee_card/assets/coffees/iced-latte.svg";
// import cappuccino from "../coffee_card/assets/coffees/cappuccino.svg";


interface AddCoffeeFormProps {
    onClose: () => void;
    onSuccess?: () => void;
    onSubmissionUpdate?: (submissionData: {
        status: string;
        statusInfo: any;
        steps: any[];
        canRetry: boolean;
    }) => void;
    // if true, auto-resume from draft without showing dialog
    autoResume?: boolean;
}

interface LocationData {
    city: string;
    country: string;
    storeCoordinates: {
        lat: number;
        lng: number;
    };
    measurementCoordinates: {
        lat: number;
        lng: number;
    };
}

// interface for nearby coffee shop (local or from Google)
interface NearbyCoffeeShop {
    _id?: string;
    name: string;
    place_id?: string;
    coordinates: {
        lat: number;
        lng: number;
    };
    distance: number;
    address: string;
    rating?: number;
    price_level?: number;
    types?: string[];
    ratingStats?: {
        totalRatings: number;
        averageRating: number;
        coffeeTypeStats?: Record<string, { count: number; averageRating: number }>;
    };
}

// create form data interface
interface CoffeeFormData {
    coffeeType: string;
    coffeeShop: string;
    coffeeShopRef?: string;  // local shop _id when from cluster-based selection
    coffeeShopDetails?: NearbyCoffeeShop;  // optional coffee shop details
    location: LocationData;
    format: { [key: string]: any };
    rating: { [key: string]: number };
    description?: string;  // optional description field
    quickRate?: number;    // optional quick rate field (0.0-10.0, decimal allowed)
    voiceBlob?: Blob;          // recorded voice description, uploaded + transcribed on submit
    voiceDurationMs?: number;  // duration of the recorded voice description
}

// mm:ss for the recorded voice-description duration
function fmtVoiceDuration(ms?: number): string {
    if (!ms || ms <= 0) return '';
    const s = Math.round(ms / 1000);
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

// Define RateCategory interface to match the format in CoffeeCardWeb
interface RateCategory {
    weight: number;
    required: boolean;
}

// rating input component with stars and slider
// removed local RatingInput in favor of shared slider components


// cold coffee patterns - everything not matching these is considered "hot"
const COLD_COFFEE_PATTERNS = ['freddo', 'iced', 'frappe', 'cold-brew', 'cold_brew'];
const isHotCoffeeType = (coffeeType: string): boolean => {
    if (!coffeeType) return false;
    const normalized = coffeeType.toLowerCase().replace(/\s+/g, '-');
    return !COLD_COFFEE_PATTERNS.some(cold => normalized.includes(cold));
};

// helper function to check if user has specific role
function checkRole(str: string | null | undefined, searchStr: string): boolean {
    if (!str) return false;
    try {
        const arr = str.split(',');
        for (let i = 0; i < arr.length; i++) {
            if (arr[i].trim() === searchStr) {
                return true;
            }
        }
        return false;
    } catch {
        return false;
    }
}

const AddCoffeeForm: React.FC<AddCoffeeFormProps> = ({ onClose, onSuccess, onSubmissionUpdate, autoResume = false }) => {
    // get user role from redux state for role-based location controls
    const user = useSelector((state: any) => state.auth);
    const canManualLocation = checkRole(user?.role, 'admin') || checkRole(user?.role, 'coffee-master');

    // reCAPTCHA v3 hook
    const { executeRecaptcha } = useGoogleReCaptcha();

    // Check if reCAPTCHA is properly loaded
    React.useEffect(() => {
            console.log('reCAPTCHA status:', {
            executeRecaptcha: !!executeRecaptcha,
            envKey: !!RECAPTCHA_SITE_KEY
        });
    }, [executeRecaptcha]);

    // form steps state
    const [currentStep, setCurrentStep] = useState(1);

    // mobile detection for responsive container
    const [isMobile, setIsMobile] = useState(false);
    useEffect(() => {
        const checkIfMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        checkIfMobile();
        window.addEventListener('resize', checkIfMobile);
        return () => window.removeEventListener('resize', checkIfMobile);
    }, []);

    // form data state — declared early so the auto-dismiss `onComplete` callback below can
    // reset it without tripping the TDZ when the hook closure is built
    const [formData, setFormData] = useState<CoffeeFormData>({
        coffeeType: '',
        coffeeShop: '',
        coffeeShopDetails: undefined, // initialize coffee shop details
        location: {
            city: '',
            country: '',
            storeCoordinates: { lat: 0, lng: 0 },
            measurementCoordinates: { lat: 0, lng: 0 }
        },
        format: {},
        rating: {},
        description: '', // initialize description
        quickRate: undefined // initialize quick rate
    });

    // whether the description step is in voice-recording mode
    const [voiceMode, setVoiceMode] = useState(false);

    // draft saving and resume functionality
    const {
        hasDraft,
        draftInfo,
        saveDraft,
        loadDraft,
        clearDraft,
        updateDraftWithCoffeeId,
        getDraftAge
    } = useCoffeeDraft();

    // ref so the auto-dismiss callback (created at hook-init time) reads the latest props
    // and helpers — onSuccess/clearDraft references would otherwise be stale after re-renders
    const completeHandlersRef = useRef({ onSuccess, clearDraft });
    completeHandlersRef.current = { onSuccess, clearDraft };

    // use new coffee submission hook — fires `onComplete` 2s after a successful submission so
    // the wizard auto-dismisses silently (failures stay sticky for retry/edit)
    const {
        isSubmitting,
        currentStatus,
        statusInfo,
        steps,
        result,
        canRetry,
        startSubmission,
        setStatus,
        completeSubmission,
        failSubmission,
        retrySubmission,
        resetSubmission,
        cleanup,
        failedFormStep,
        errorMessage,
        errorDetails
    } = useCoffeeSubmission({
        autoDismissMs: 2000,
        onComplete: () => {
            const { onSuccess: parentOnSuccess, clearDraft: clearLocalDraft } = completeHandlersRef.current;
            clearLocalDraft();
            setFormData(DEFAULT_FORM_DATA);
            parentOnSuccess?.();
        }
    });

    // show resume dialog state
    const [showResumeDialog, setShowResumeDialog] = useState(false);
    const [draftChecked, setDraftChecked] = useState(false);

    // newly created coffee ID for image upload
    const [newCoffeeId, setNewCoffeeId] = useState<string | null>(null);
    const [folderPath, setFolderPath] = useState<string | null>(null);

    // image upload state
    const [selectedImage, setSelectedImage] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Add new states for location fetching
    const [isGettingLocation, setIsGettingLocation] = useState(false);
    const [locationError, setLocationError] = useState<string | null>(null);

    // GPS location state
    const [hasGpsLocation, setHasGpsLocation] = useState(false);

    // nearby coffee shops state
    const [nearbyCoffeeShops, setNearbyCoffeeShops] = useState<NearbyCoffeeShop[]>([]);
    const [nearbyClusterId, setNearbyClusterId] = useState<string | null>(null);
    const [isLoadingCoffeeShops, setIsLoadingCoffeeShops] = useState(false);
    const [coffeeShopSearch, setCoffeeShopSearch] = useState('');
    const [showAddShopDialog, setShowAddShopDialog] = useState(false);
    const [shopListFilter, setShopListFilter] = useState('');
    const [clusterStatus, setClusterStatus] = useState<'none' | 'initializing' | 'ready'>('ready');

    // inline Google-by-name shop search (skips the add-new dialog)
    const [googleShopResults, setGoogleShopResults] = useState<NearbyCoffeeShop[]>([]);
    const [isSearchingGoogleShops, setIsSearchingGoogleShops] = useState(false);
    const [googleShopsSearched, setGoogleShopsSearched] = useState(false);
    const [proposingPlaceId, setProposingPlaceId] = useState<string | undefined>(undefined);

    // admin override coords (only used when canManualLocation && operator pastes lat,lng)
    const [adminOverride, setAdminOverride] = useState<{ lat: number; lng: number } | null>(null);

    // 'initializing' polling refs
    const initPollTimerRef = useRef<NodeJS.Timeout | null>(null);
    const initPollStartRef = useRef<number | null>(null);
    const autoGpsFiredRef = useRef(false);

    // service type state (dine-in, takeaway, delivery)
    const [serviceType, setServiceType] = useState<'dine-in' | 'takeaway' | 'delivery' | ''>('');

    // home made coffee state (admin/coffee-master users, plus verified machine owners)
    const [isHomeMade, setIsHomeMade] = useState(false);

    // supermarket coffee state (kind: 'supermarket') — a packaged/branded coffee carrying
    // a brand + a product type (milk/sugar/iced format) instead of a shop + sugar/milk
    // format. Open to everyone and rendered through the SAME 4-step wizard as a shop
    // rating: location+photo → brand → type → rating.
    const [isSupermarket, setIsSupermarket] = useState(false);
    const [brand, setBrand] = useState<BrandPickerValue | null>(null);
    const [selectedType, setSelectedType] = useState<SupermarketType | null>(null);

    // Verified-owner gate for the machine picker. The `coffeeMachineOwner` flag lives on the
    // backend User model (not in the JWT / auth slice), so we probe the gated GET /machines:
    // 200 ⇒ verified owner (admins pass the same middleware), 401/403/error ⇒ not an owner.
    const [isVerifiedOwner, setIsVerifiedOwner] = useState(false);
    const [myMachines, setMyMachines] = useState<CoffeeMachine[]>([]);
    const [selectedMachineId, setSelectedMachineId] = useState<string | undefined>(undefined);
    const [homeBrew, setHomeBrew] = useState<HomeBrewState>(EMPTY_HOME_BREW);
    // registered bean/capsule attribution for the home flow (verified owners only)
    const [myBeans, setMyBeans] = useState<CoffeeBean[]>([]);
    const [myCapsules, setMyCapsules] = useState<CoffeeCapsule[]>([]);
    const [selectedBeanId, setSelectedBeanId] = useState<string | undefined>(undefined);
    const [selectedCapsuleId, setSelectedCapsuleId] = useState<string | undefined>(undefined);

    useEffect(() => {
        let cancelled = false;
        listMyMachines()
            .then((list) => {
                if (cancelled) return;
                setMyMachines(list.filter((m) => m.isActive));
                setIsVerifiedOwner(true);
                // verified owners can also attribute a registered bean/capsule — fetch both
                // active lists once (best-effort; an empty/failed list just hides the picker).
                listMyBeans()
                    .then((beans) => {
                        if (!cancelled) setMyBeans(beans.filter((b) => b.isActive));
                    })
                    .catch(() => {});
                listMyCapsules()
                    .then((caps) => {
                        if (!cancelled) setMyCapsules(caps.filter((c) => c.isActive));
                    })
                    .catch(() => {});
            })
            .catch(() => {
                // 401/403 (not a verified owner) or transient error — leave the home flow unchanged.
            });
        return () => {
            cancelled = true;
        };
    }, []);

    // Verified machine owners also reach the home flow (so they can attach a machine + brew);
    // admin/coffee-master keep the role-based access they already had.
    const canAddHomeMade =
        checkRole(user?.role, 'admin') || checkRole(user?.role, 'coffee-master') || isVerifiedOwner;

    const handleSelectMachine = (id: string | undefined) => {
        setSelectedMachineId(id);
        setHomeBrew(brewStateFromMachine(myMachines.find((m) => m._id === id)));
        // changing the machine flips which picker is shown — drop a now-irrelevant
        // bean/capsule selection so it can't leak into the payload.
        setSelectedBeanId(undefined);
        setSelectedCapsuleId(undefined);
    };

    const handleBrewChange = (key: keyof HomeBrewState, value: string) =>
        setHomeBrew((prev) => ({ ...prev, [key]: value }));

    // keep track of slider offsets for centered rating categories (e.g., acidity)
    const [centeredSliderPositions, setCenteredSliderPositions] = useState<Record<string, number>>({});

    // debounced manual geocode timer
    const geocodeTimer = useRef<NodeJS.Timeout | null>(null);

    // function to handle custom coffee type submission
    const handleCustomCoffeeSubmission = async (coffeeName: string) => {
        if (!coffeeName.trim()) return;

        setIsSubmittingCustomCoffee(true);
        try {
            const response = await apiClient.get(`${url}${coffeeTypePaths.requests()}?coffeeName=${encodeURIComponent(coffeeName.trim())}`);

            if (response.status === 200) {
                // set the custom coffee name as the coffee type
                setFormData(prev => ({
                    ...prev,
                    coffeeType: coffeeName.trim()
                }));
                setCustomCoffeeName('');
                setShowOtherInput(false);
                // success notification could be added here
            } else {
                console.error('Failed to submit custom coffee type request');
            }
        } catch (error) {
            console.error('Error submitting custom coffee type:', error);
        } finally {
            setIsSubmittingCustomCoffee(false);
        }
    };

    // function to handle coffee type selection
    const handleCoffeeTypeSelect = (coffeeType: string) => {
        if (coffeeType === 'other') {
            setShowOtherInput(true);
            setFormData(prev => ({ ...prev, coffeeType: '', format: {} }));
            setCoffeeTypeSelected(false);
            setFormatOptionsSelected(false);
        } else {
            setShowOtherInput(false);
            setFormData(prev => ({ ...prev, coffeeType: coffeeType, format: {} }));
            setCoffeeTypeSelected(true);
            setFormatOptionsSelected(false);
            // Don't auto-complete format options when coffee type changes
            // setTimeout(() => checkAndUpdateFormatCompletion(), 0);
        }
    };

    // function to reset coffee type selection
    const handleChooseAgainCoffee = () => {
        setCoffeeTypeSelected(false);
        setFormData(prev => ({ ...prev, coffeeType: '', format: {} }));
        setFormatOptionsSelected(false);
    };

    const [ratingMode, setRatingMode] = useState<'quick' | 'detailed'>('detailed');

    // states for "other" coffee type
    const [showOtherInput, setShowOtherInput] = useState(false);
    const [customCoffeeName, setCustomCoffeeName] = useState('');
    const [isSubmittingCustomCoffee, setIsSubmittingCustomCoffee] = useState(false);

    // coffee types display control
    const [coffeeTypeSelected, setCoffeeTypeSelected] = useState(false);
    const [formatOptionsSelected, setFormatOptionsSelected] = useState(false);

    // coffee types: each row carries userRatingCount + isFavorite from /types/with-counts.
    // Pre-sorted by the API: favorites (top-5) first, then rated by count desc, then never-rated alphabetical.
    const [coffeeTypes, setCoffeeTypes] = useState<any[]>([]);
    const [loadingCoffeeTypes, setLoadingCoffeeTypes] = useState(true);

    // coffee icon cache to avoid repeated API calls
    const [coffeeIconCache, setCoffeeIconCache] = useState<{ [key: string]: string }>({});
    const [coffeeTypeIconMapping, setCoffeeTypeIconMapping] = useState<{ [key: string]: string }>({});
    const [iconUrls, setIconUrls] = useState<{ [key: string]: string }>({});

    // function to get coffee icon for a specific coffee type
    const getCoffeeIcon = async (coffeeType: string): Promise<string> => {
        // if we have it in cache, return it
        if (coffeeIconCache[coffeeType]) {
            return coffeeIconCache[coffeeType];
        }

        // get icon name from mapping
        const iconName = coffeeTypeIconMapping[coffeeType] || 'espresso';

        try {
            const iconUrl = await fetchIcon(iconName);
            if (iconUrl) {
                setCoffeeIconCache(prev => ({ ...prev, [coffeeType]: iconUrl }));
                return iconUrl;
            }
        } catch (error) {
            console.error(`Failed to fetch icon for ${coffeeType}:`, error);
        }

        // fallback to espresso icon
        if (!coffeeIconCache['espresso']) {
            try {
                const fallbackUrl = await fetchIcon('espresso');
                if (fallbackUrl) {
                    setCoffeeIconCache(prev => ({ ...prev, espresso: fallbackUrl }));
                    return fallbackUrl;
                }
            } catch (error) {
                console.error('Failed to fetch fallback espresso icon:', error);
            }
        }

        return coffeeIconCache['espresso'] || '';
    };

    // Initialize coffee icons cache and load coffee types.
    // Per-mount fetch (no module-level cache guard) so admin edits show without a hard reload.
    useEffect(() => {
        const initializeCoffeeData = async () => {
            try {
                setLoadingCoffeeTypes(true);

                // /types provides the full coffee objects (coffeeFormat, rateCategories, displayName)
                // needed for FormatOptions and the coffeeTypesCache.
                // /types/with-counts overlays per-user userRatingCount + isFavorite, and the
                // canonical section ordering (favorites → rated desc → never-rated alphabetical).
                const token = getAuthToken();
                const authHeaders = token ? { Authorization: `Bearer ${token}` } : undefined;
                const [typesResponse, withCountsResponse] = await Promise.all([
                    apiClient.get(`${url}${coffeeTypePaths.list()}`, { headers: authHeaders }),
                    token
                        ? apiClient.get(`${url}${coffeeTypePaths.withCounts()}`, { headers: authHeaders }).catch(() => null)
                        : Promise.resolve(null),
                ]);

                if (typesResponse.data.success) {
                    const fullByKey: Record<string, any> = {};
                    (typesResponse.data.data || []).forEach((coffee: any) => {
                        fullByKey[coffee.coffeeKey] = coffee;
                    });

                    // populate the global cache for format definitions - crucial for FormatOptions
                    coffeeTypesCache = { ...fullByKey };
                    coffeeTypesFetched = true;

                    // build the ordered list. Prefer the with-counts ordering; fall back to /types
                    // (which already returns isActive types) when the user is anonymous or the
                    // with-counts call failed.
                    let ordered: any[];
                    const wc = withCountsResponse?.data;
                    if (wc && Array.isArray(wc.types)) {
                        const favSet = new Set<string>(wc.favorites || []);
                        ordered = wc.types
                            .map((row: any) => {
                                const base = fullByKey[row.coffeeKey];
                                if (!base) return null;
                                return {
                                    ...base,
                                    userRatingCount: row.userRatingCount ?? 0,
                                    isFavorite: favSet.has(row.coffeeKey),
                                };
                            })
                            .filter(Boolean);
                    } else {
                        ordered = (typesResponse.data.data || [])
                            .slice()
                            .sort((a: any, b: any) => (a.coffeeKey || '').localeCompare(b.coffeeKey || ''))
                            .map((coffee: any) => ({
                                ...coffee,
                                userRatingCount: 0,
                                isFavorite: false,
                            }));
                    }
                    setCoffeeTypes(ordered);

                    const iconMap: { [key: string]: string } = {};
                    const typeIconMap: { [key: string]: string } = {};
                    const urlMap: { [key: string]: string } = {};

                    // build mapping from coffee type to icon name
                    (typesResponse.data.data || []).forEach((coffee: any) => {
                        typeIconMap[coffee.coffeeKey] = coffee.iconName;
                    });
                    setCoffeeTypeIconMapping(typeIconMap);

                    // fetch and cache all icons sequentially to avoid overwhelming the server
                    for (const coffee of (typesResponse.data.data || [])) {
                        try {
                            const iconUrl = await fetchIcon(coffee.iconName);
                            if (iconUrl) {
                                iconMap[coffee.coffeeKey] = iconUrl;
                                urlMap[coffee.coffeeKey] = iconUrl;
                            }
                        } catch (error) {
                            console.error(`Failed to fetch icon for ${coffee.coffeeKey}:`, error);
                        }
                    }

                    setCoffeeIconCache(iconMap);
                    setIconUrls(urlMap);
                }
            } catch (error) {
                console.error('Failed to fetch coffee types:', error);
            } finally {
                setLoadingCoffeeTypes(false);
            }
        };

        initializeCoffeeData();
    }, []);

    // Generate required rating fields based on selected coffee type
    useEffect(() => {
        const initializeCoffeeType = async () => {
            if (formData.coffeeType) {
                // Ensure coffee types are loaded
                await fetchCoffeeTypesData();

                if (coffeeTypesCache[formData.coffeeType]) {
                    // Clear existing ratings when coffee type changes to make them truly optional
                    setFormData(prev => ({
                        ...prev,
                        rating: {} // start with empty ratings - let user add them manually
                    }));
                }
            }
        };

        initializeCoffeeType();
    }, [formData.coffeeType]);

    // forward submission status updates to parent component. The callback goes
    // through a ref because the coffee page passes a new function every render:
    // with it in the deps, each parent render re-ran this effect, the page stored
    // the status object in state and re-rendered, and that looped for as long as
    // the form stayed open.
    const submissionUpdateRef = useRef(onSubmissionUpdate);
    submissionUpdateRef.current = onSubmissionUpdate;
    useEffect(() => {
        const forwardUpdate = submissionUpdateRef.current;
        if (forwardUpdate) {
            forwardUpdate({
                status: currentStatus,
                statusInfo,
                steps,
                canRetry
            });
        }
    }, [currentStatus, statusInfo, steps, canRetry]);

    // Don't auto-complete format options when format data changes
    // useEffect(() => {
    //     if (formData.coffeeType && coffeeTypesFetched) {
    //         checkAndUpdateFormatCompletion();
    //     }
    // }, [formData.format, formData.coffeeType, coffeeTypesFetched]);

    // Create preview URL for selected image
    useEffect(() => {
        if (selectedImage) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreviewUrl(reader.result as string);
            };
            reader.readAsDataURL(selectedImage);
        } else {
            setPreviewUrl(null);
        }
    }, [selectedImage]);

    // ref to track if we need to auto-resume and if we've already resumed.
    // Within the (1h) draft window we ALWAYS auto-restore silently — no resume dialog.
    // The `autoResume` prop is kept for callers but auto-resume is now the default; an
    // expired draft is already cleared on load by the hook's expiry check.
    const shouldAutoResumeRef = useRef(true);
    shouldAutoResumeRef.current = true;
    const hasAutoResumedRef = useRef(false);

    // check for existing draft on mount
    useEffect(() => {
        if (!draftChecked && hasDraft && draftInfo) {
            if (shouldAutoResumeRef.current) {
                // auto-resume without showing dialog - will be handled in next effect
                setDraftChecked(true);
            } else {
                setShowResumeDialog(true);
                setDraftChecked(true);
            }
        } else if (!draftChecked) {
            setDraftChecked(true);
        }
    }, [hasDraft, draftInfo, draftChecked]);

    // save draft when step changes or form data changes significantly.
    // Debounced (~400ms) so high-frequency edits (keystrokes / slider ticks) coalesce into a
    // single localStorage write — the persisted base64 image makes per-change writes wasteful
    // and risks quota thrash. Persistence now covers the supermarket flow too (brand/type).
    useEffect(() => {
        // don't save draft during initial load or if submission is in progress.
        if (!draftChecked || isSubmitting || currentStatus === 'completed') return;

        // only save if we have meaningful data (at least past step 1 with image, any step with
        // shop/home data, or a started supermarket entry).
        const hasFormData = formData.coffeeType || formData.coffeeShop || formData.location.city;
        const hasSupermarketData = isSupermarket && (!!brand?.brandRef || !!brand?.brandName || !!selectedType);
        const shouldSave = (currentStep > 1 && previewUrl) || hasFormData || hasSupermarketData;

        if (!shouldSave) return;

        const timer = setTimeout(() => {
            const draft: CoffeeDraft = {
                step: currentStep,
                formData,
                imagePreview: previewUrl,
                timestamp: Date.now(),
                coffeeShopSearch,
                hasGpsLocation,
                isHomeMade,
                ratingMode,
                coffeeId: newCoffeeId || undefined,
                serviceType: serviceType || undefined,
                selectedMachineId,
                selectedBeanId,
                selectedCapsuleId,
                isSupermarket,
                brandRef: brand?.brandRef,
                brandName: brand?.brandName,
                typeKey: selectedType?.key || undefined,
                typeName: selectedType?.name,
                typeFormat: selectedType?.format,
            };
            saveDraft(draft);
        }, 400);

        return () => clearTimeout(timer);
    }, [currentStep, formData, previewUrl, coffeeShopSearch, hasGpsLocation, isHomeMade, isSupermarket, brand, selectedType, selectedMachineId, selectedBeanId, selectedCapsuleId, ratingMode, newCoffeeId, serviceType, draftChecked, isSubmitting, currentStatus, saveDraft]);

    // handle resuming from draft
    const handleResumeDraft = async () => {
        const draft = loadDraft();
        if (!draft) {
            setShowResumeDialog(false);
            return;
        }

        // restore form state
        setFormData(draft.formData);
        setCoffeeShopSearch(draft.coffeeShopSearch || '');
        setHasGpsLocation(draft.hasGpsLocation);
        setIsHomeMade(draft.isHomeMade);
        setRatingMode(draft.ratingMode);
        if (draft.serviceType) setServiceType(draft.serviceType);

        // restore the verified-owner home attribution (machine + bean/capsule refs). Brew
        // text fields live in component state and aren't persisted; the selected machine still
        // re-renders the right picker so the resumed bean/capsule choice shows correctly.
        if (draft.selectedMachineId) setSelectedMachineId(draft.selectedMachineId);
        if (draft.selectedBeanId) setSelectedBeanId(draft.selectedBeanId);
        if (draft.selectedCapsuleId) setSelectedCapsuleId(draft.selectedCapsuleId);

        // restore the supermarket wizard flow (brand + selected product type)
        if (draft.isSupermarket) {
            setIsSupermarket(true);
            if (draft.brandRef || draft.brandName) {
                setBrand({ brandRef: draft.brandRef, brandName: draft.brandName });
            }
            if (draft.typeName && draft.typeFormat) {
                setSelectedType({
                    key: draft.typeKey || '',
                    name: draft.typeName,
                    format: draft.typeFormat,
                });
            }
        }

        // restore image if available
        if (draft.imagePreview) {
            setPreviewUrl(draft.imagePreview);
            // convert base64 back to file
            const file = await base64ToFile(draft.imagePreview);
            if (file) {
                setSelectedImage(file);
            }
        }

        // handle partial submission recovery
        if (draft.coffeeId) {
            setNewCoffeeId(draft.coffeeId);
            // skip to image upload step (step 1) since coffee entry exists
            setCurrentStep(1);
            toast.info('Coffee entry exists. Please upload the image to complete.');
        } else {
            // go to the saved step
            setCurrentStep(draft.step);
        }

        // update coffee type selection state if we have a coffee type
        if (draft.formData.coffeeType) {
            setCoffeeTypeSelected(true);
        }

        // check format completion if we have format data
        if (draft.formData.coffeeType && Object.keys(draft.formData.format).length > 0) {
            const completed = checkFormatCompletion(draft.formData.format, draft.formData.coffeeType);
            setFormatOptionsSelected(completed);
        }

        setShowResumeDialog(false);
    };

    // handle discarding draft
    const handleDiscardDraft = () => {
        clearDraft();
        setShowResumeDialog(false);
    };

    // auto-resume from draft once the draft check has run. Within the 1h window this fires
    // silently (no dialog); after 1h the draft is already expired + cleared so hasDraft is false.
    useEffect(() => {
        if (shouldAutoResumeRef.current && draftChecked && hasDraft && !showResumeDialog && !hasAutoResumedRef.current) {
            hasAutoResumedRef.current = true;
            handleResumeDraft();
        }
    }, [draftChecked, hasDraft, showResumeDialog]);

    // Location-first: auto-fire GPS once on step 1 unless we already have coords or are
    // home-made or have an outstanding error. Supermarket now goes through the SAME
    // location step (no GPS bypass) so it auto-fires too. Fires only after the draft
    // check has run so a resumed draft's stored coords aren't overwritten.
    useEffect(() => {
        if (
            currentStep === 1 &&
            draftChecked &&
            !autoGpsFiredRef.current &&
            !hasGpsLocation &&
            !isHomeMade &&
            !isGettingLocation &&
            !locationError
        ) {
            autoGpsFiredRef.current = true;
            getCurrentLocation();
        }
    }, [currentStep, draftChecked, hasGpsLocation, isHomeMade, isGettingLocation, locationError]);

    // Cancel any pending init-poll on unmount.
    useEffect(() => {
        return () => {
            if (initPollTimerRef.current) {
                clearTimeout(initPollTimerRef.current);
                initPollTimerRef.current = null;
            }
        };
    }, []);

    // Note: Coffee types are now fetched in the icon initialization effect above

    // Handle input changes
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;

        // handle nested properties (location data)
        if (name.includes('.')) {
            const [parent, child] = name.split('.');

            if (parent === 'location') {
                setFormData(prev => ({
                    ...prev,
                    location: {
                        ...prev.location,
                        [child]: value
                    }
                }));
            }
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    // Handle rating changes - new component passes numbers directly
    const handleRatingChange = (category: string, value: number) => {
        // value is already clamped by the component, but ensure it's stored as number
        setFormData(prev => ({
            ...prev,
            rating: {
                ...prev.rating,
                [category]: value
            }
        }));
    };

    // handle centered slider change which gives us both rating and position
    const handleCenteredSliderChange = (category: string) => (rating: number, position: number) => {
        setCenteredSliderPositions(prev => ({ ...prev, [category]: position }));
        handleRatingChange(category, rating);
    };

    // Handle format changes
    const handleFormatChange = (key: string, value: string) => {
        setFormData(prev => {
            const newData = {
                ...prev,
                format: {
                    ...prev.format,
                    [key]: value
                }
            };

            // Don't auto-complete format options - let user explicitly finish
            // const completed = checkFormatCompletion(newData.format, prev.coffeeType);
            // setFormatOptionsSelected(completed);

            return newData;
        });
    };

    // Function to check if all available format options are selected (pure function)
    const checkFormatCompletion = (format: any, coffeeType: string): boolean => {
        if (!coffeeType || !coffeeTypesCache[coffeeType]) return true;

        const availableFormats = coffeeTypesCache[coffeeType].coffeeFormat || {};
        const selectedFormats = format || {};

        let allSelected = true;

        // Check each available format category
        Object.keys(availableFormats).forEach(category => {
            if (category === 'sugar') {
                const sugarOptions = availableFormats[category] as { level?: string[], type?: string[] };
                if (sugarOptions.level && sugarOptions.level.length > 0) {
                    // require sugar level
                    if (!selectedFormats.sugar?.level) {
                        allSelected = false;
                    }
                    // also require sugar type if level is not 'no-sugar'
                    if (selectedFormats.sugar?.level && selectedFormats.sugar.level !== 'no-sugar' && !selectedFormats.sugar?.type) {
                        allSelected = false;
                    }
                }
            } else if (category === 'milk') {
                const milkOptions = availableFormats[category] as { level?: string[], type?: string[] };
                if (milkOptions.type && milkOptions.type.length > 0) {
                    if (!selectedFormats.milk?.type) {
                        allSelected = false;
                    }
                }
            } else if (category === 'ice-cream') {
                const iceCreamOptions = availableFormats[category] as { level?: string[], type?: string[] };
                if (iceCreamOptions.type && iceCreamOptions.type.length > 0) {
                    if (!selectedFormats['ice-cream']?.type) {
                        allSelected = false;
                    }
                }
            } else if (category === 'type' && Array.isArray(availableFormats[category]) && availableFormats[category].length > 0) {
                if (!selectedFormats.type) {
                    allSelected = false;
                }
            }
        });

        return allSelected;
    };

    // Function to check if all available format options are selected (stateful version)
    const checkAndUpdateFormatCompletion = () => {
        const completed = checkFormatCompletion(formData.format, formData.coffeeType);
        setFormatOptionsSelected(completed);
    };

    // Handle sugar selection from icon selector
    const handleSugarChange = (level: string, type: string) => {
        setFormData(prev => {
            const newData = {
                ...prev,
                format: {
                    ...prev.format,
                    sugar: {
                        level: level,
                        type: type || undefined
                    }
                }
            };

            // Check completion immediately with the new data
            const completed = checkFormatCompletion(newData.format, formData.coffeeType);
            setFormatOptionsSelected(completed);

            return newData;
        });
    };

    // Handle nested format changes
    const handleNestedFormatChange = (category: string, subcategory: string, value: string) => {
        setFormData(prev => {
            const newFormat = { ...prev.format };

            if (!newFormat[category]) {
                newFormat[category] = {};
            }

            if (typeof newFormat[category] === 'object') {
                newFormat[category] = {
                    ...newFormat[category],
                    [subcategory]: value
                };
            }

            const newData = {
                ...prev,
                format: newFormat
            };

            // Don't auto-complete format options - let user explicitly finish
            // const completed = checkFormatCompletion(newData.format, prev.coffeeType);
            // setFormatOptionsSelected(completed);

            return newData;
        });
    };

    // Handle image selection
    const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            setSelectedImage(e.target.files[0]);
        }
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(false);

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            setSelectedImage(e.dataTransfer.files[0]);
        }
    };

    const clearInitPoll = () => {
        if (initPollTimerRef.current) {
            clearTimeout(initPollTimerRef.current);
            initPollTimerRef.current = null;
        }
        initPollStartRef.current = null;
    };

    const fetchNearbyCoffeeShops = async (
        latitude: number,
        longitude: number,
        opts?: { adminLat?: number; adminLng?: number; isPoll?: boolean }
    ) => {
        if (!opts?.isPoll) {
            setIsLoadingCoffeeShops(true);
        }
        try {
            const params: Record<string, string | number> = { lat: latitude, lng: longitude };
            if (typeof opts?.adminLat === 'number' && typeof opts?.adminLng === 'number') {
                params.adminLat = opts.adminLat;
                params.adminLng = opts.adminLng;
            }
            const response = await apiClient.get(`${url}${coffeeShopPaths.nearby()}`, {
                params,
                headers: { Authorization: `Bearer ${getAuthToken()}` }
            });
            if (response.data?.coffeeShops && Array.isArray(response.data.coffeeShops)) {
                setNearbyCoffeeShops(response.data.coffeeShops);
            } else {
                setNearbyCoffeeShops([]);
            }
            setNearbyClusterId(response.data?.clusterId || null);
            const nextStatus: 'none' | 'initializing' | 'ready' =
                response.data?.clusterStatus === 'none' || response.data?.clusterStatus === 'initializing'
                    ? response.data.clusterStatus
                    : 'ready';
            setClusterStatus(nextStatus);

            // schedule a refetch every 3s while initializing, capped at 30s total
            if (nextStatus === 'initializing') {
                if (initPollStartRef.current === null) initPollStartRef.current = Date.now();
                const elapsed = Date.now() - initPollStartRef.current;
                if (elapsed < 30_000) {
                    if (initPollTimerRef.current) clearTimeout(initPollTimerRef.current);
                    initPollTimerRef.current = setTimeout(() => {
                        fetchNearbyCoffeeShops(latitude, longitude, {
                            adminLat: opts?.adminLat,
                            adminLng: opts?.adminLng,
                            isPoll: true
                        });
                    }, 3_000);
                } else {
                    clearInitPoll();
                }
            } else {
                clearInitPoll();
            }
        } catch (error) {
            console.error('Error fetching nearby coffee shops:', error);
            setNearbyCoffeeShops([]);
        } finally {
            if (!opts?.isPoll) {
                setIsLoadingCoffeeShops(false);
            }
        }
    };

    // search via Google Places when user types (same as "Add new coffee shop" dialog), not DB/cluster
    const searchCoffeeShops = async (searchTerm: string) => {
        if (!searchTerm.trim() || !formData.location.storeCoordinates.lat) {
            return;
        }

        setIsLoadingCoffeeShops(true);

        try {
            const lat = formData.location.storeCoordinates.lat;
            const lng = formData.location.storeCoordinates.lng;
            const response = await apiClient.post(
                `${url}${coffeeShopPaths.searchGoogle()}`,
                { query: searchTerm.trim(), lat, lng },
                { headers: { Authorization: `Bearer ${getAuthToken()}` } }
            );

            const candidates = response.data?.candidates ?? [];
            const mapped: NearbyCoffeeShop[] = candidates.map((c: { place_id: string; name: string; geometry: { location: { lat: number; lng: number } }; formatted_address?: string; vicinity?: string; rating?: number; price_level?: number; types?: string[] }) => ({
                name: c.name,
                place_id: c.place_id,
                coordinates: { lat: c.geometry.location.lat, lng: c.geometry.location.lng },
                distance: 0,
                address: c.formatted_address || c.vicinity || '',
                rating: c.rating,
                price_level: c.price_level,
                types: c.types || []
            }));
            setNearbyCoffeeShops(mapped);
        } catch (error) {
            console.error('Error searching coffee shops:', error);
            setNearbyCoffeeShops([]);
        } finally {
            setIsLoadingCoffeeShops(false);
        }
    };

    // Function to get current location from GPS
    const getCurrentLocation = () => {
        if (!navigator.geolocation) {
            setLocationError("Geolocation is not supported by your browser");
            return;
        }

        setIsGettingLocation(true);
        setLocationError(null);

        navigator.geolocation.getCurrentPosition(
            (position) => {
                // success callback
                const { latitude, longitude } = position.coords;

                // update both store and measurement coordinates with the same values
                // user can adjust them later if needed
                setFormData(prev => ({
                    ...prev,
                    location: {
                        ...prev.location,
                        storeCoordinates: {
                            lat: latitude,
                            lng: longitude
                        },
                        measurementCoordinates: {
                            lat: latitude,
                            lng: longitude
                        }
                    }
                }));

                setHasGpsLocation(true);
                setAdminOverride(null);
                clearInitPoll();
                initPollStartRef.current = null;

                // Automatically fill location using reverse geocoding for all users
                fetchLocationDetails(latitude, longitude);

                // Fetch nearby coffee shops for both user types
                fetchNearbyCoffeeShops(latitude, longitude);

                setIsGettingLocation(false);
            },
            (error) => {
                // error callback
                let errorMessage = "Unable to retrieve your location";
                switch (error.code) {
                    case error.PERMISSION_DENIED:
                        errorMessage = "Location access was denied. Please enable location permissions.";
                        break;
                    case error.POSITION_UNAVAILABLE:
                        errorMessage = "Location information is unavailable.";
                        break;
                    case error.TIMEOUT:
                        errorMessage = "The request to get location timed out.";
                        break;
                }
                setLocationError(errorMessage);
                setIsGettingLocation(false);
            },
            {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 0
            }
        );
    };

    // Admin-only manual override: bypass GPS by setting coords directly and re-running nearby
    const handleManualOverride = (lat: number, lng: number) => {
        setLocationError(null);
        setHasGpsLocation(true);
        setAdminOverride({ lat, lng });
        clearInitPoll();
        initPollStartRef.current = null;
        setFormData(prev => ({
            ...prev,
            coffeeShop: '',
            coffeeShopRef: undefined,
            coffeeShopDetails: undefined,
            location: {
                ...prev.location,
                storeCoordinates: { lat, lng },
                measurementCoordinates: { lat, lng }
            }
        }));
        fetchLocationDetails(lat, lng);
        fetchNearbyCoffeeShops(lat, lng, { adminLat: lat, adminLng: lng });
    };

    const handleStoreCoordinatesCommit = (lat: number, lng: number) => {
        setLocationError(null);
        setHasGpsLocation(true);
        clearInitPoll();
        initPollStartRef.current = null;
        setCoffeeShopSearch('');
        setFormData(prev => ({
            ...prev,
            coffeeShop: '',
            coffeeShopRef: undefined,
            coffeeShopDetails: undefined,
            location: {
                ...prev.location,
                storeCoordinates: { lat, lng },
                measurementCoordinates: { lat, lng }
            }
        }));
        fetchLocationDetails(lat, lng);
        if (canManualLocation) {
            setAdminOverride({ lat, lng });
        }
        fetchNearbyCoffeeShops(
            lat,
            lng,
            canManualLocation ? { adminLat: lat, adminLng: lng } : undefined
        );
    };

    // Function to get city and country from coordinates using a free geocoding service
    const fetchLocationDetails = async (latitude: number, longitude: number) => {
        try {
            const response = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=10`);
            const data = await response.json();

            if (data && data.address) {
                // extract city with multiple fallback options
                const city = data.address.city ||
                    data.address.town ||
                    data.address.village ||
                    data.address.municipality ||
                    data.address.county ||
                    '';

                // extract country with multiple fallback options
                const country = data.address.country ||
                    data.address.country_code?.toUpperCase() ||
                    data.address.state ||
                    '';

                setFormData(prev => ({
                    ...prev,
                    location: {
                        ...prev.location,
                        city: city,
                        country: country
                    }
                }));
            }
        } catch (error) {
            console.error("Error fetching location details:", error);
            // Don't show an error to the user - the coordinates are still set
        }
    };

    // Function to compress image before upload
    const compressImage = async (file: File, maxWidth: number = 800, quality: number = 0.8): Promise<File> => {
        return new Promise((resolve) => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const img = new Image();

            img.onload = () => {
                // Calculate new dimensions while maintaining aspect ratio
                const aspectRatio = img.width / img.height;
                let { width, height } = img;

                if (width > maxWidth) {
                    width = maxWidth;
                    height = width / aspectRatio;
                }

                canvas.width = width;
                canvas.height = height;

                // Draw and compress
                ctx?.drawImage(img, 0, 0, width, height);

                canvas.toBlob((blob) => {
                    if (blob) {
                        const compressedFile = new File([blob], file.name, {
                            type: 'image/jpeg',
                            lastModified: Date.now()
                        });
                        resolve(compressedFile);
                    } else {
                        resolve(file); // fallback to original if compression fails
                    }
                }, 'image/jpeg', quality);
            };

            img.src = URL.createObjectURL(file);
        });
    };

    // enhanced helper that creates the coffee entry and uploads the image with detailed progress tracking
    const finalizeCoffeeEntry = async () => {
        // start submission process
        startSubmission();

        // Keep reference to status update functions
        const statusUpdateRef = {
            setStatus,
            completeSubmission,
            failSubmission,
            onSubmissionUpdate
        };

        // don't close modal immediately - let user see progress and handle errors
        // modal will close only on successful completion via onSuccess callback

        if (!selectedImage) {
            failSubmission('please select an image first', false, 'No image was selected for upload', 1);
            return;
        }

        try {
            let recaptchaToken = null;

            // check if this is a partial recovery (coffee entry already exists, just need image)
            if (newCoffeeId) {
                console.log('Partial recovery detected - skipping to image upload for coffeeId:', newCoffeeId);
                statusUpdateRef.setStatus('uploading', 'resuming image upload...', 60);

                // compress and upload image directly
                const compressedImage = await compressImage(selectedImage, 800, 0.8);
                console.log(`Compressed size: ${(compressedImage.size / 1024 / 1024).toFixed(2)}MB`);

                const imgData = new FormData();
                imgData.append('image', compressedImage);

                const uploadResp = await apiClient.post(`${url}${coffeeRatingPaths.image(newCoffeeId)}`, imgData, {
                    headers: {
                        Authorization: `Bearer ${getAuthToken()}`
                    },
                    timeout: 30000
                });

                statusUpdateRef.setStatus('processing', 'finalizing entry...', 90);
                statusUpdateRef.completeSubmission({
                    coffeeId: newCoffeeId,
                    message: 'Image uploaded successfully! Coffee entry complete.'
                });

                // submission hook will fire `onComplete` after the auto-dismiss delay,
                // which clears the draft and notifies the parent to close + refetch
                return;
            }

            // Execute reCAPTCHA if available
            if (executeRecaptcha) {
                statusUpdateRef.setStatus('validating', 'verifying security...', 5);
                try {
                    recaptchaToken = await executeRecaptcha('coffee_creation');
                    console.log('reCAPTCHA token generated successfully:', !!recaptchaToken);
                } catch (error) {
                    console.warn('reCAPTCHA execution failed:', error);
                    // Continue without reCAPTCHA as backend allows it for backward compatibility
                }
            } else {
                console.warn('reCAPTCHA not ready, proceeding without it');
            }

            // step 2: prepare and validate data
            statusUpdateRef.setStatus('validating', 'preparing submission data...', 10);

            // flatten the format data for the api
            const flattenedFormat: Record<string, string> = {};
            if (formData.format) {
                Object.entries(formData.format).forEach(([key, value]) => {
                    if (key === 'sugar' && typeof value === 'object') {
                        if (value.level) flattenedFormat['sugar-level'] = value.level;
                        if (value.type) flattenedFormat['sugar-type'] = value.type;
                    } else if (key === 'milk' && typeof value === 'object') {
                        if (value.type) flattenedFormat['milk-type'] = value.type;
                    } else if (key === 'ice-cream' && typeof value === 'object') {
                        if (value.type) flattenedFormat['ice-cream-type'] = value.type;
                    } else {
                        flattenedFormat[key] = String(value);
                    }
                });
            }

            // include service type in format if set
            if (serviceType) {
                flattenedFormat['service-type'] = serviceType;
            }

            // step 2: create the coffee entry
            statusUpdateRef.setStatus('creating', 'creating coffee entry in database...', 30);
            const headers = {};

            // only include ratings that the user has actually set (value > 0)
            const actualRatings: Record<string, number> = {};
            Object.entries(formData.rating).forEach(([category, value]) => {
                if (value > 0) {
                    actualRatings[category] = value;
                }
            });

            // Prepare location data - use defaults for home-made coffee
            const locationData = {
                ...formData.location,
                city: formData.location.city || (isHomeMade ? 'admin-undisclosed' : ''),
                country: formData.location.country || (isHomeMade ? 'admin-undisclosed' : ''),
                storeCoordinates: {
                    lat: formData.location.storeCoordinates.lat || 0,
                    lng: formData.location.storeCoordinates.lng || 0
                },
                measurementCoordinates: {
                    lat: formData.location.measurementCoordinates.lat || 0,
                    lng: formData.location.measurementCoordinates.lng || 0
                }
            };

            // verified owners may attach a registered machine + brew to a home rating; the
            // backend ignores `home` for non-owners, and a verified user who picks no machine
            // sends nothing (existing home flow unchanged).
            const selectedMachine = isHomeMade && isVerifiedOwner && selectedMachineId
                ? myMachines.find(m => m._id === selectedMachineId)
                : undefined;
            // attribute a registered bean (manual/other machines) or capsule (capsule
            // machines) when one was picked. Bare id strings — the backend populates on read.
            const beanRef = selectedMachine && (selectedMachine.type === 'manual' || selectedMachine.type === 'other')
                ? selectedBeanId
                : undefined;
            const capsuleRef = selectedMachine && selectedMachine.type === 'capsule'
                ? selectedCapsuleId
                : undefined;
            const homeBlock = selectedMachine
                ? {
                    machineRef: selectedMachine._id,
                    beanRef,
                    capsuleRef,
                    brew: buildHomeBrew(selectedMachine, homeBrew),
                }
                : undefined;

            const payload = {
                coffeeType: formData.coffeeType,
                coffeeShop: formData.coffeeShop,
                coffeeShopRef: formData.coffeeShopRef || undefined,
                coffeeShopDetails: formData.coffeeShopDetails || undefined, // include place_id and details
                location: locationData,
                format: flattenedFormat,
                rating: actualRatings, // only send ratings that have been set
                description: formData.description || undefined,
                quickRate: typeof formData.quickRate === 'number' ? formData.quickRate : undefined,
                home: homeBlock, // verified-owner machine + brew (undefined otherwise)
                recaptchaToken: recaptchaToken // include reCAPTCHA token (may be null)
            };

            console.log('Sending payload with reCAPTCHA token:', !!recaptchaToken);
            console.log('Home made coffee:', isHomeMade);
            console.log('Location data:', locationData);
            if (recaptchaToken) {
                console.log('reCAPTCHA token length:', recaptchaToken.length);
            }

            const createResp = await apiClient.post(`${url}${coffeeRatingPaths.create()}`, payload, headers);

            if (!createResp.data || !createResp.data.coffeeRateId) {
                throw new Error('server did not return a valid coffee ID');
            }

            const createdCoffeeId = createResp.data.coffeeRateId;

            // save coffeeId to draft for recovery if image upload fails
            updateDraftWithCoffeeId(createdCoffeeId);

            // step 2.5: if the description was recorded, upload + transcribe it now (sets the
            // cleaned description + fun description + stored audio). non-fatal — a transcription
            // hiccup must not lose the whole coffee post.
            if (formData.voiceBlob) {
                statusUpdateRef.setStatus('uploading', 'transcribing your voice description...', 60);
                try {
                    const voiceData = new FormData();
                    const vt = formData.voiceBlob.type;
                    const ext = vt.includes('mp4') ? 'm4a' : vt.includes('ogg') ? 'ogg' : 'webm';
                    voiceData.append('audio', formData.voiceBlob, `voice.${ext}`);
                    if (formData.voiceDurationMs) {
                        voiceData.append('durationMs', String(Math.round(formData.voiceDurationMs)));
                    }
                    await apiClient.post(`${url}${coffeeRatingPaths.voice(createdCoffeeId)}`, voiceData, {
                        headers: { Authorization: `Bearer ${getAuthToken()}` },
                        timeout: 60000,
                    });
                } catch (voiceErr) {
                    console.error('voice description upload failed (keeping the post):', voiceErr);
                }
            }

            // step 3: compress and upload the image
            statusUpdateRef.setStatus('uploading', 'compressing and uploading coffee image...', 70);

            // Compress image before upload
            const compressedImage = await compressImage(selectedImage, 800, 0.8);
            console.log(`Original size: ${(selectedImage.size / 1024 / 1024).toFixed(2)}MB`);
            console.log(`Compressed size: ${(compressedImage.size / 1024 / 1024).toFixed(2)}MB`);

            const imgData = new FormData();
            imgData.append('image', compressedImage);
            if (recaptchaToken) {
                imgData.append('recaptchaToken', recaptchaToken);
                console.log('Including reCAPTCHA token in image upload');
            } else {
                console.log('Proceeding with image upload without reCAPTCHA token');
            }

            const uploadResp = await apiClient.post(`${url}${coffeeRatingPaths.image(createdCoffeeId)}`, imgData, {
                headers: {
                    Authorization: `Bearer ${getAuthToken()}`
                },
                timeout: 30000 // 30 second timeout for image upload
            });

            // step 4: finalize
            statusUpdateRef.setStatus('processing', 'finalizing entry...', 90);

            statusUpdateRef.completeSubmission({
                coffeeId: createdCoffeeId,
                message: 'coffee entry created successfully!'
            });

            // submission hook will fire `onComplete` after the auto-dismiss delay,
            // which clears the draft and notifies the parent to close + refetch
        } catch (error: any) {
            console.error('Error finalizing coffee entry:', error);

            // enhanced error handling with specific error messages and context
            let errorMessage = 'an error occurred creating the coffee entry';
            let errorDetails: string | undefined;
            let failedStep = 4; // default to rating step

            if (error.response) {
                // server responded with error status. backend uses { error, detail } shape.
                const serverError = error.response.data?.error || error.response.data?.message;
                const serverDetail = error.response.data?.detail || error.response.data?.error_description;
                if (error.response.status === 401) {
                    errorMessage = 'authentication failed - please log in again';
                    errorDetails = serverDetail || 'Your session has expired or is invalid';
                } else if (error.response.status === 403) {
                    errorMessage = serverError || 'permission denied - missing required scope';
                    errorDetails = serverDetail || `Server responded with status 403`;
                } else if (error.response.status === 413) {
                    errorMessage = 'image file is too large - please choose a smaller image';
                    errorDetails = serverDetail || 'Maximum file size is 10MB';
                    failedStep = 1;
                } else if (error.response.status === 429) {
                    errorMessage = 'too many requests - please wait and try again';
                    errorDetails = serverDetail || 'Rate limit exceeded';
                } else if (serverError) {
                    errorMessage = serverError;
                    errorDetails = serverDetail || `Server responded with status ${error.response.status}`;
                } else {
                    errorDetails = `Server responded with status ${error.response.status}`;
                }
            } else if (error.request) {
                // network error
                errorMessage = 'network error - please check your connection and try again';
                errorDetails = 'Failed to reach the server';
            } else if (error.code === 'ECONNABORTED') {
                // timeout error
                errorMessage = 'upload timed out - please try again with a smaller image';
                errorDetails = 'Request exceeded 30 second timeout';
                failedStep = 1;
            }

            // allow retry for most errors except authentication
            const allowRetry = error.response?.status !== 401;
            statusUpdateRef.failSubmission(errorMessage, allowRetry, errorDetails, failedStep);
        }
    };

    // Submit a supermarket coffee: POST /ratings/supermarket (create-or-get brand by name,
    // or use the picked brandRef; existing type → typeKey, custom type → typeName+typeFormat),
    // then the SAME image (+ optional voice) upload tail the shop/home flow uses. Drives the
    // shared useCoffeeSubmission status machine.
    const finalizeSupermarketEntry = async () => {
        startSubmission();
        const statusUpdateRef = { setStatus, completeSubmission, failSubmission, onSubmissionUpdate };

        if (!selectedImage) {
            failSubmission('please select an image first', false, 'No image was selected for upload', 1);
            return;
        }
        if (!brand || (!brand.brandRef && !brand.brandName)) {
            failSubmission('please choose a coffee brand', false, undefined, 2);
            return;
        }
        if (!selectedType) {
            failSubmission('please choose a coffee type', false, undefined, 3);
            return;
        }

        try {
            // optional reCAPTCHA — attached only to the image upload (mirrors the normal flow);
            // the supermarket create endpoint does not require a token.
            let recaptchaToken: string | null = null;
            if (executeRecaptcha) {
                statusUpdateRef.setStatus('validating', 'verifying security...', 5);
                try {
                    recaptchaToken = await executeRecaptcha('coffee_creation');
                } catch (err) {
                    console.warn('reCAPTCHA execution failed:', err);
                }
            }

            statusUpdateRef.setStatus('creating', 'creating coffee entry in database...', 30);
            const payload: SupermarketRatingPayload = {
                description: formData.description || undefined,
            };
            if (brand.brandRef) payload.brandRef = brand.brandRef;
            else payload.brandName = brand.brandName;

            // existing catalog type → typeKey; custom type → typeName + typeFormat (create-or-get pending)
            if (selectedType.key) {
                payload.typeKey = selectedType.key;
            } else {
                payload.typeName = selectedType.name;
                payload.typeFormat = selectedType.format;
            }

            // general location where the rating happened — city only, no shop, no map
            if (formData.location.city) payload.city = formData.location.city;
            if (formData.location.country) payload.country = formData.location.country;

            // send EITHER a single quickRate OR the type's format-derived categories
            if (ratingMode === 'quick') {
                if (typeof formData.quickRate === 'number') payload.quickRate = formData.quickRate;
            } else {
                const criteria = supermarketRateCategories(selectedType.format);
                const rating: Record<string, number> = {};
                Object.keys(criteria).forEach((k) => {
                    if ((formData.rating[k] || 0) > 0) rating[k] = formData.rating[k];
                });
                if (Object.keys(rating).length > 0) payload.rating = rating;
            }

            const { coffeeRateId } = await submitSupermarketRating(payload);
            if (!coffeeRateId) throw new Error('server did not return a valid coffee ID');

            // optional voice description (non-fatal) — same endpoint as the normal flow
            if (formData.voiceBlob) {
                statusUpdateRef.setStatus('uploading', 'transcribing your voice description...', 60);
                try {
                    const voiceData = new FormData();
                    const vt = formData.voiceBlob.type;
                    const ext = vt.includes('mp4') ? 'm4a' : vt.includes('ogg') ? 'ogg' : 'webm';
                    voiceData.append('audio', formData.voiceBlob, `voice.${ext}`);
                    if (formData.voiceDurationMs) {
                        voiceData.append('durationMs', String(Math.round(formData.voiceDurationMs)));
                    }
                    await apiClient.post(`${url}${coffeeRatingPaths.voice(coffeeRateId)}`, voiceData, {
                        headers: { Authorization: `Bearer ${getAuthToken()}` },
                        timeout: 60000,
                    });
                } catch (voiceErr) {
                    console.error('voice description upload failed (keeping the post):', voiceErr);
                }
            }

            // image upload — identical pipeline to the shop/home flow
            statusUpdateRef.setStatus('uploading', 'compressing and uploading coffee image...', 70);
            const compressedImage = await compressImage(selectedImage, 800, 0.8);
            const imgData = new FormData();
            imgData.append('image', compressedImage);
            if (recaptchaToken) imgData.append('recaptchaToken', recaptchaToken);
            await apiClient.post(`${url}${coffeeRatingPaths.image(coffeeRateId)}`, imgData, {
                headers: { Authorization: `Bearer ${getAuthToken()}` },
                timeout: 30000,
            });

            statusUpdateRef.setStatus('processing', 'finalizing entry...', 90);
            statusUpdateRef.completeSubmission({
                coffeeId: coffeeRateId,
                message: 'coffee entry created successfully!',
            });
        } catch (error: any) {
            console.error('Error finalizing supermarket entry:', error);
            let msg = 'an error occurred creating the coffee entry';
            let details: string | undefined;
            if (error.response) {
                const serverError = error.response.data?.error || error.response.data?.message;
                const serverDetail = error.response.data?.detail || error.response.data?.error_description;
                if (error.response.status === 401) {
                    msg = 'authentication failed - please log in again';
                    details = serverDetail || 'Your session has expired or is invalid';
                } else if (error.response.status === 413) {
                    msg = 'image file is too large - please choose a smaller image';
                    details = serverDetail || 'Maximum file size is 10MB';
                } else if (serverError) {
                    msg = serverError;
                    details = serverDetail || `Server responded with status ${error.response.status}`;
                } else {
                    details = `Server responded with status ${error.response.status}`;
                }
            } else if (error.request) {
                msg = 'network error - please check your connection and try again';
                details = 'Failed to reach the server';
            }
            const allowRetry = error.response?.status !== 401;
            statusUpdateRef.failSubmission(msg, allowRetry, details, 1);
        }
    };

    // Enhanced form validation
    const validateForm = (): string | null => {
        // Supermarket goes through the SAME 4-step wizard, but each step gates on its own
        // fields: step1 location+photo (no shop), step2 brand, step3 type, step4 rating.
        if (isSupermarket) {
            if (currentStep === 1) {
                if (!canManualLocation) {
                    if (!hasGpsLocation || formData.location.storeCoordinates.lat === 0) {
                        return 'we need your location to add a coffee — please allow location access';
                    }
                }
                if (!selectedImage) {
                    return 'please select an image first';
                }
            } else if (currentStep === 2) {
                if (!brand || (!brand.brandRef && !brand.brandName)) {
                    return 'please choose a coffee brand';
                }
            } else if (currentStep === 3) {
                if (!selectedType) {
                    return 'please choose a coffee type';
                }
            } else if (currentStep === 4) {
                if (ratingMode === 'quick') {
                    if (typeof formData.quickRate !== 'number' || formData.quickRate < 0 || formData.quickRate > 10) {
                        return 'please provide a quick rate between 0 and 10';
                    }
                } else {
                    const hasDetailedRating = Object.keys(formData.rating).filter((k) => formData.rating[k] > 0).length > 0;
                    const hasDescription =
                        (formData.description && formData.description.trim().length > 0) || !!formData.voiceBlob;
                    if (!hasDetailedRating && !hasDescription) {
                        return 'please provide either a detailed rating or a description for the coffee';
                    }
                }
            }
            return null;
        }

        if (currentStep === 1) {
            if (!isHomeMade) {
                if (clusterStatus === 'initializing') {
                    return "we're still fetching nearby coffee shops — please wait a moment";
                }
                if (!hasGpsLocation || formData.location.storeCoordinates.lat === 0) {
                    return 'we need your location to add a coffee — please allow location access';
                }
                if (!formData.coffeeShop) {
                    return 'please select a coffee shop from the nearby list';
                }
            }
            if (!selectedImage) {
                return 'please select an image first';
            }
        } else if (currentStep === 2) {
            // Step 2: Location + Coffee Shop validation
            if (!formData.coffeeShop && !isHomeMade) {
                return 'please select a coffee shop';
            }

            // Skip location validation for admin/coffee-master users (they can set it later)
            if (!canManualLocation) {
                if (!formData.location.city || !formData.location.country) {
                    return 'please provide location information (city and country)';
                }

                // Skip coordinate validation for home made coffee
                if (!isHomeMade) {
                    // Regular users must use GPS to get location
                    if (!hasGpsLocation) {
                        return 'please get your GPS location first by clicking "Get Current Location"';
                    }
                    if (formData.location.storeCoordinates.lat === 0 || formData.location.storeCoordinates.lng === 0) {
                        return 'GPS location is required. Please enable location access and try again';
                    }
                }
            }
        } else if (currentStep === 3) {
            // Step 3: Coffee Type + Format validation
            if (!formData.coffeeType) {
                return 'please select a coffee type';
            }

            // Validate format options if available
            if (formData.coffeeType && coffeeTypesCache[formData.coffeeType]) {
                const availableFormats = coffeeTypesCache[formData.coffeeType].coffeeFormat || {};
                if (Object.keys(availableFormats).length > 0) {
                    // Check if all format options are selected
                    const selectedFormats = formData.format || {};
                    let missingFormats: string[] = [];

                    Object.keys(availableFormats).forEach(category => {
                        if (category === 'sugar') {
                            const sugarOptions = availableFormats[category] as { level?: string[], type?: string[] };
                            if (sugarOptions.level && sugarOptions.level.length > 0) {
                                if (!selectedFormats.sugar?.level) {
                                    missingFormats.push('sugar level');
                                }
                            }
                        } else if (category === 'milk') {
                            const milkOptions = availableFormats[category] as { level?: string[], type?: string[] };
                            if (milkOptions.type && milkOptions.type.length > 0) {
                                if (!selectedFormats.milk?.type) {
                                    missingFormats.push('milk type');
                                }
                            }
                        } else if (category === 'ice-cream') {
                            const iceCreamOptions = availableFormats[category] as { level?: string[], type?: string[] };
                            if (iceCreamOptions.type && iceCreamOptions.type.length > 0) {
                                if (!selectedFormats['ice-cream']?.type) {
                                    missingFormats.push('ice cream flavor');
                                }
                            }
                        } else if (category === 'type' && Array.isArray(availableFormats[category]) && availableFormats[category].length > 0) {
                            if (!selectedFormats.type) {
                                missingFormats.push('brew method');
                            }
                        }
                    });

                    if (missingFormats.length > 0) {
                        return `please select the following format options: ${missingFormats.join(', ')}`;
                    }
                }
            }
        } else if (currentStep === 4) {
            // validate rating - either detailed rating, quick rate, or description
            if (ratingMode === 'quick') {
                if (typeof formData.quickRate !== 'number' || formData.quickRate < 0 || formData.quickRate > 10) {
                    return 'please provide a quick rate between 0 and 10';
                }
            } else {
                const hasDetailedRating = Object.keys(formData.rating).length > 0;
                const hasDescription =
                    (formData.description && formData.description.trim().length > 0) || !!formData.voiceBlob;

                if (!hasDetailedRating && !hasDescription) {
                    return 'please provide either a detailed rating or a description for the coffee';
                }
            }
        }
        return null;
    };

    // Handle form submission
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Enhanced validation
        const validationError = validateForm();
        if (validationError) {
            failSubmission(validationError, false); // don't allow retry for validation errors
            return;
        }

        if (currentStep === 1) {
            // move to location step
            setCurrentStep(2);
        } else if (currentStep === 2) {
            // move to coffee type step
            setCurrentStep(3);
        } else if (currentStep === 3) {
            // move to rating step
            setCurrentStep(4);
        } else if (currentStep === 4) {
            // Finalize the coffee entry with enhanced error handling. Supermarket goes through
            // the same step transitions but finalizes via its own create endpoint at step 4.
            try {
                if (isSupermarket) {
                    await finalizeSupermarketEntry();
                } else {
                    await finalizeCoffeeEntry();
                }
            } catch (error) {
                console.error('Submission failed:', error);
                // error handling is already done in finalize*Entry
            }
        }
    };

    // Handle going back to previous step
    const handlePreviousStep = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    };

    // Check if form is valid
    const isFormValid = () => {
        const { coffeeType, coffeeShop, location } = formData;

        // Supermarket path — same 4 steps, gated per-step on its own fields.
        if (isSupermarket) {
            if (currentStep === 1) {
                if (!selectedImage) return false;
                if (canManualLocation) return true;
                return hasGpsLocation && formData.location.storeCoordinates.lat !== 0;
            } else if (currentStep === 2) {
                return !!brand && (!!brand.brandRef || !!brand.brandName);
            } else if (currentStep === 3) {
                return !!selectedType;
            } else if (currentStep === 4) {
                if (ratingMode === 'quick') {
                    const qr = formData.quickRate;
                    return typeof qr === 'number' && qr >= 0 && qr <= 10;
                }
                const hasDetailedRating = Object.keys(formData.rating).filter((k) => formData.rating[k] > 0).length > 0;
                const hasDescription =
                    !!(formData.description && formData.description.trim().length > 0) || !!formData.voiceBlob;
                return hasDetailedRating || hasDescription;
            }
            return false;
        }

        // Role-based location validation - skip coordinates for home made coffee
        let locationValid = false;
        if (isHomeMade) {
            // Home made coffee doesn't need coordinates, only basic location info
            locationValid = !!location.city && !!location.country;
        } else if (!canManualLocation) {
            // Regular users must use GPS
            locationValid = hasGpsLocation && !!location.city && !!location.country &&
                !!location.storeCoordinates.lat && !!location.storeCoordinates.lng;
        } else {
            // Admin/coffee-master can use GPS or manual entry, but it's not required to proceed
            locationValid = true;
        }

        if (currentStep === 1) {
            if (!selectedImage) return false;
            if (isHomeMade) return true;
            if (clusterStatus === 'initializing') return false;
            if (!hasGpsLocation || formData.location.storeCoordinates.lat === 0) return false;
            if (!coffeeShop) return false;
            return true;
        } else if (currentStep === 2) {
            // Step 2: Location + Coffee Shop
            const shopValid = !!coffeeShop || isHomeMade;
            if (!shopValid) return false;

            // Only check location for non-admin users
            if (!canManualLocation && !locationValid) {
                return false;
            }
            return true;
        } else if (currentStep === 3) {
            // Step 3: Coffee Type + Format
            const coffeeTypeValid = !!coffeeType;
            if (!coffeeTypeValid) return false;

            // Check format options if available
            if (coffeeType && coffeeTypesCache[coffeeType]) {
                const availableFormats = coffeeTypesCache[coffeeType].coffeeFormat || {};
                if (Object.keys(availableFormats).length > 0) {
                    const selectedFormats = formData.format || {};

                    // Check if all format options are selected
                    let allFormatSelected = true;
                    Object.keys(availableFormats).forEach(category => {
                        if (category === 'sugar') {
                            const sugarOptions = availableFormats[category] as { level?: string[], type?: string[] };
                            if (sugarOptions.level && sugarOptions.level.length > 0) {
                                if (!selectedFormats.sugar?.level) {
                                    allFormatSelected = false;
                                }
                            }
                        } else if (category === 'milk') {
                            const milkOptions = availableFormats[category] as { level?: string[], type?: string[] };
                            if (milkOptions.type && milkOptions.type.length > 0) {
                                if (!selectedFormats.milk?.type) {
                                    allFormatSelected = false;
                                }
                            }
                        } else if (category === 'ice-cream') {
                            const iceCreamOptions = availableFormats[category] as { level?: string[], type?: string[] };
                            if (iceCreamOptions.type && iceCreamOptions.type.length > 0) {
                                if (!selectedFormats['ice-cream']?.type) {
                                    allFormatSelected = false;
                                }
                            }
                        } else if (category === 'type' && Array.isArray(availableFormats[category]) && availableFormats[category].length > 0) {
                            if (!selectedFormats.type) {
                                allFormatSelected = false;
                            }
                        }
                    });

                    return allFormatSelected;
                }
            }

            return true;
        } else if (currentStep === 4) {
            // Step 4: Rating + Description
            if (ratingMode === 'quick') {
                const qr = formData.quickRate;
                return typeof qr === 'number' && qr >= 0 && qr <= 10;
            } else {
                const hasDetailedRating = Object.keys(formData.rating).filter(key => formData.rating[key] > 0).length > 0;
                const hasDescription =
                    !!(formData.description && formData.description.trim().length > 0) || !!formData.voiceBlob;
                return hasDetailedRating || hasDescription;
            }
        }

        return false;
    };

    // Prevent background scrolling when modal is open and cleanup submission state
    React.useEffect(() => {
        // Disable body scrolling when component mounts
        document.body.style.overflow = 'hidden';

        // Re-enable body scrolling when component unmounts and cleanup submission state
        return () => {
            document.body.style.overflow = 'auto';
            cleanup();
        };
    }, [cleanup]);

    // Prevent clicks inside the modal from closing it
    const handleModalClick = (e: React.MouseEvent) => {
        e.stopPropagation();
    };

    // Handle coffee shop input and search with debouncing
    const handleCoffeeShopChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value || '';
        setCoffeeShopSearch(value);
        setFormData(prev => ({
            ...prev,
            coffeeShop: value
        }));

        // clear existing timeout
        if (geocodeTimer.current) {
            clearTimeout(geocodeTimer.current);
        }

        // set new timeout for search
        geocodeTimer.current = setTimeout(() => {
            if (value.trim()) {
                searchCoffeeShops(value);
            } else {
                // if search is empty, fetch nearby shops
                if (formData.location.storeCoordinates.lat) {
                    fetchNearbyCoffeeShops(formData.location.storeCoordinates.lat, formData.location.storeCoordinates.lng);
                }
            }
        }, 500);
    };

    // Handle coffee shop selection from dropdown
    const handleCoffeeShopSelect = (shop: NearbyCoffeeShop) => {
        if (!shop || !shop.name) return;

        setFormData(prev => ({
            ...prev,
            coffeeShop: shop.name,
            coffeeShopRef: shop._id,
            coffeeShopDetails: {
                place_id: shop.place_id,
                name: shop.name,
                coordinates: {
                    lat: shop.coordinates.lat,
                    lng: shop.coordinates.lng
                },
                distance: shop.distance,
                address: shop.address,
                rating: shop.rating,
                price_level: shop.price_level,
                types: shop.types || []
            },
            location: {
                ...prev.location,
                storeCoordinates: {
                    lat: shop.coordinates.lat,
                    lng: shop.coordinates.lng
                }
            }
        }));
        setCoffeeShopSearch(shop.name);
        // clear any inline Google results once a shop is chosen
        setGoogleShopResults([]);
        setGoogleShopsSearched(false);
    };

    // inline Google search by the typed filter name — no dialog, distance shown
    const handleInlineGoogleSearch = async (query: string) => {
        if (!query.trim()) return;
        const originLat = formData.location.storeCoordinates.lat;
        const originLng = formData.location.storeCoordinates.lng;
        setIsSearchingGoogleShops(true);
        setGoogleShopResults([]);
        try {
            const candidates = await searchGoogleShops(query, originLat, originLng);
            const mapped: NearbyCoffeeShop[] = candidates.map(c => {
                const lat = c.geometry.location.lat;
                const lng = c.geometry.location.lng;
                const distance = (originLat || originLng)
                    ? Math.round(haversineKm({ lat: originLat, lng: originLng }, { lat, lng }) * 1000)
                    : 0;
                return {
                    name: c.name,
                    place_id: c.place_id,
                    coordinates: { lat, lng },
                    distance,
                    address: c.formatted_address || c.vicinity || '',
                    rating: c.rating,
                    price_level: c.price_level
                };
            });
            // closest first
            mapped.sort((a, b) => a.distance - b.distance);
            setGoogleShopResults(mapped);
            setGoogleShopsSearched(true);
        } catch (err) {
            console.error('Inline Google search failed:', err);
            toast.error('Search failed');
            setGoogleShopResults([]);
            setGoogleShopsSearched(true);
        } finally {
            setIsSearchingGoogleShops(false);
        }
    };

    // pick a Google result inline: propose it, then select it like a nearby shop
    const handleSelectGoogleResult = async (result: NearbyCoffeeShop) => {
        if (!result.place_id) return;
        setProposingPlaceId(result.place_id);
        try {
            const proposed = await proposeGoogleCandidate(
                {
                    place_id: result.place_id,
                    name: result.name,
                    geometry: { location: { lat: result.coordinates.lat, lng: result.coordinates.lng } },
                    formatted_address: result.address,
                    rating: result.rating,
                    price_level: result.price_level
                },
                {
                    city: formData.location.city,
                    country: formData.location.country,
                    clusterId: nearbyClusterId || undefined
                }
            );
            if (proposed) {
                // keep the distance we computed (propose returns 0)
                const withDistance: NearbyCoffeeShop = { ...proposed, distance: result.distance };
                handleCoffeeShopSelect(withDistance);
                setNearbyCoffeeShops(prev => [withDistance, ...prev]);
            }
        } catch (err) {
            console.error('Propose from inline search failed:', err);
            toast.error('Failed to add shop');
        } finally {
            setProposingPlaceId(undefined);
        }
    };

    const handleQuickRateChange = (value: number) => {
        setFormData(prev => ({
            ...prev,
            quickRate: value
        }));
    };

    // handle home made coffee checkbox
    const handleHomeMadeChange = (checked: boolean) => {
        setIsHomeMade(checked);
        if (checked) {
            // set coffee shop to "Home Made" and set default home coordinates
            setFormData(prev => ({
                ...prev,
                coffeeShop: 'Home Made',
                location: {
                    ...prev.location,
                    // Set default coordinates for home (can be user's current location or default)
                    storeCoordinates: { lat: 0, lng: 0 }, // home doesn't need precise coordinates
                    measurementCoordinates: { lat: 0, lng: 0 }
                }
            }));
            setCoffeeShopSearch('Home Made');
        } else {
            // clear coffee shop when unchecked and reset coordinates
            setFormData(prev => ({
                ...prev,
                coffeeShop: '',
                coffeeShopRef: undefined,
                coffeeShopDetails: undefined,
                location: {
                    ...prev.location,
                    storeCoordinates: { lat: 0, lng: 0 },
                    measurementCoordinates: { lat: 0, lng: 0 }
                }
            }));
            setCoffeeShopSearch('');
            // drop any attached machine + brew — this is no longer a home rating
            setSelectedMachineId(undefined);
            setHomeBrew(EMPTY_HOME_BREW);
        }
    };

    // switch between the café/shop flow and the supermarket flow. Both now run through the
    // SAME 4-step wizard (location+photo → brand → type → rating), so entering supermarket
    // just resets to step 1 and drops any shop/home selection; it does NOT bypass the GPS
    // gate — the auto-GPS effect fires getCurrentLocation (which also fills the city).
    // Leaving it clears the brand/type for a clean shop entry.
    const handleSelectMode = (mode: 'shop' | 'supermarket') => {
        if (mode === 'supermarket') {
            setIsSupermarket(true);
            setIsHomeMade(false);
            setCurrentStep(1);
            setFormData(prev => ({
                ...prev,
                coffeeShop: '',
                coffeeShopRef: undefined,
                coffeeShopDetails: undefined,
                coffeeType: '',
                format: {},
            }));
            setCoffeeShopSearch('');
            setSelectedMachineId(undefined);
            setHomeBrew(EMPTY_HOME_BREW);
            setBrand(null);
            setSelectedType(null);
        } else {
            setIsSupermarket(false);
            setBrand(null);
            setSelectedType(null);
        }
    };

    // description / voice block — shared by the step-4 rating screen and the supermarket flow.
    const descriptionBlock = formData.voiceBlob ? (
        <div className="space-y-2">
            <label className="text-xs text-gray-500">description (voice)</label>
            <div className="flex items-center gap-2 text-xs text-gray-600 border-2 border-ink rounded-sm px-2 py-1.5 bg-accent/10">
                <span>🎙️ voice description recorded{formData.voiceDurationMs ? ` (${fmtVoiceDuration(formData.voiceDurationMs)})` : ''}</span>
                <button
                    type="button"
                    onClick={() => setFormData((prev) => ({ ...prev, voiceBlob: undefined, voiceDurationMs: undefined }))}
                    className="ml-auto underline text-red-600"
                >
                    remove
                </button>
            </div>
            <p className="text-[10px] text-gray-400">the fun description appears on your card once it finishes uploading.</p>
        </div>
    ) : voiceMode ? (
        <div className="space-y-2">
            <label className="text-xs text-gray-500">record a description</label>
            <div className="flex items-center gap-2 border-2 border-ink rounded-sm px-2 py-1.5">
                <VoiceRecorder
                    onSend={(blob, durationMs) => {
                        setFormData((prev) => ({ ...prev, voiceBlob: blob, voiceDurationMs: durationMs }));
                        setVoiceMode(false);
                    }}
                    onCancel={() => setVoiceMode(false)}
                    sendLabel="use recording"
                />
            </div>
        </div>
    ) : (
        <div className="space-y-2">
            <DescriptionInput
                value={formData.description || ''}
                onChange={(v) => setFormData((prev) => ({ ...prev, description: v }))}
            />
            <button
                type="button"
                onClick={() => setVoiceMode(true)}
                className="text-xs text-gray-500 underline"
            >
                🎙️ or record a voice description
            </button>
        </div>
    );

    // footer nav: shop and supermarket BOTH use the same 4-step indicator + nav; only the
    // supermarket step labels differ (Photo·Brand·Type·Rate vs Image·Location·Coffee·Rating).
    const navInner = (
        <div className="flex flex-col gap-2">
            <StepIndicator
                currentStep={currentStep}
                onStepClick={(step) => {
                    if (step < currentStep) {
                        setCurrentStep(step);
                    }
                }}
                disabled={isSubmitting}
                compact
                labels={isSupermarket ? ['Photo', 'Brand', 'Type', 'Rate'] : undefined}
            />
            <StepNavigation
                currentStep={currentStep}
                isSubmitting={isSubmitting}
                isFormValid={isFormValid()}
                onBack={handlePreviousStep}
                onNext={() => {
                    const event = new Event('submit') as any;
                    handleSubmit(event);
                }}
                // on mobile this bar is the sheet footer, which never scrolls
                iosTapOverlay={isMobile}
            />
        </div>
    );

    const formContent = (
        <>
            <form className="add-coffee-form" onSubmit={handleSubmit} onClick={handleModalClick}>
                {/* in-form submission status */}
                {isSubmitting && currentStatus !== 'idle' && (
                    <div className="mb-4">
                        <CoffeeStatusIndicator
                            status={currentStatus}
                            inline
                            message={statusInfo.message}
                            progress={statusInfo.progress}
                            showProgress
                        />
                    </div>
                )}

                {/* Step 1: entry mode + (shop/home location-first OR single-screen supermarket) */}
                {currentStep === 1 && (
                    <section className="space-y-5">
                        <ModeSelector
                            mode={isSupermarket ? 'supermarket' : 'shop'}
                            onChange={handleSelectMode}
                            disabled={isSubmitting}
                        />

                        {isSupermarket ? (
                            <>
                                {/* same location-first step as a shop visit, minus the nearby-shop list */}
                                <div className="space-y-3">
                                    <header>
                                        <h3 className="text-sm font-semibold text-gray-700 lowercase">where are you?</h3>
                                    </header>

                                    <LocationControls
                                        isHomeMade={false}
                                        hasGpsLocation={hasGpsLocation}
                                        isGettingLocation={isGettingLocation}
                                        locationError={locationError}
                                        locationText={`${formData.location.city}, ${formData.location.country}`}
                                        onGetLocation={getCurrentLocation}
                                        coordinates={formData.location.storeCoordinates}
                                        isAdmin={canManualLocation}
                                        onManualOverride={handleManualOverride}
                                        onStoreCoordinatesCommit={handleStoreCoordinatesCommit}
                                    />
                                </div>

                                <div className="space-y-3">
                                    <header>
                                        <h3 className="text-sm font-semibold text-gray-700 lowercase">upload coffee image</h3>
                                    </header>
                                    <ImageUploadSection
                                        previewUrl={previewUrl}
                                        isDragging={isDragging}
                                        onDragOver={handleDragOver}
                                        onDragLeave={handleDragLeave}
                                        onDrop={handleDrop}
                                        onClick={() => fileInputRef.current?.click()}
                                        inputRef={fileInputRef}
                                        onFileChange={handleImageSelect}
                                    />
                                </div>
                            </>
                        ) : (
                          <>
                        {canAddHomeMade && (
                            <div className="p-4 border border-ink/10 bg-muted/20 rounded-xl">
                                <label className="flex items-start gap-3 cursor-pointer">
                                    <Checkbox
                                        checked={isHomeMade}
                                        onCheckedChange={(checked) => handleHomeMadeChange(checked === true)}
                                        className="mt-0.5"
                                    />
                                    <div className="flex flex-col gap-1 flex-1">
                                        <span className="text-sm font-medium text-foreground">🏠 home made coffee</span>
                                        <span className="text-xs text-muted-foreground">
                                            (Admin/Coffee-Master only)
                                        </span>
                                    </div>
                                </label>
                            </div>
                        )}

                        {!isHomeMade && (
                            <div className="space-y-3">
                                <header>
                                    <h3 className="text-sm font-semibold text-gray-700 lowercase">where are you?</h3>
                                </header>

                                <LocationControls
                                    isHomeMade={isHomeMade}
                                    hasGpsLocation={hasGpsLocation}
                                    isGettingLocation={isGettingLocation}
                                    locationError={locationError}
                                    locationText={`${formData.location.city}, ${formData.location.country}`}
                                    onGetLocation={getCurrentLocation}
                                    coordinates={formData.location.storeCoordinates}
                                    isAdmin={canManualLocation}
                                    onManualOverride={handleManualOverride}
                                    onStoreCoordinatesCommit={handleStoreCoordinatesCommit}
                                />

                                {hasGpsLocation && !formData.coffeeShop && (
                                    <CoffeeShopList
                                        shops={nearbyCoffeeShops}
                                        isLoading={isLoadingCoffeeShops}
                                        hasLocation={hasGpsLocation || formData.location.storeCoordinates.lat !== 0}
                                        selectedPlaceId={formData.coffeeShopRef || formData.coffeeShopDetails?.place_id}
                                        onSelect={handleCoffeeShopSelect}
                                        showAdminStats={canManualLocation}
                                        isAdmin={canManualLocation}
                                        maxShops={30}
                                        onAddNewShop={() => setShowAddShopDialog(true)}
                                        searchValue={shopListFilter}
                                        onSearchChange={setShopListFilter}
                                        clusterStatus={clusterStatus}
                                        onGoogleSearch={handleInlineGoogleSearch}
                                        googleResults={googleShopResults}
                                        isSearchingGoogle={isSearchingGoogleShops}
                                        googleSearched={googleShopsSearched}
                                        onSelectGoogleResult={handleSelectGoogleResult}
                                        proposingPlaceId={proposingPlaceId}
                                    />
                                )}

                                {hasGpsLocation && formData.coffeeShop && (
                                    <div className="flex items-center justify-between gap-3 p-3 border border-mint/30 bg-mint/5 rounded-lg">
                                        <span className="text-sm">
                                            Selected: <strong>{formData.coffeeShop}</strong>
                                        </span>
                                        <Button
                                            type="button"
                                            variant="destructive"
                                            size="sm"
                                            onClick={() => {
                                                setFormData(prev => ({
                                                    ...prev,
                                                    coffeeShop: '',
                                                    coffeeShopRef: undefined,
                                                    coffeeShopDetails: undefined
                                                }));
                                                setCoffeeShopSearch('');
                                            }}
                                        >
                                            ✕
                                        </Button>
                                    </div>
                                )}

                                <AddCoffeeShopDialog
                                    open={showAddShopDialog}
                                    onClose={() => setShowAddShopDialog(false)}
                                    onShopAdded={(shop) => {
                                        handleCoffeeShopSelect(shop);
                                        setNearbyCoffeeShops(prev => [shop, ...prev]);
                                        const baseLat = formData.location.storeCoordinates.lat;
                                        const baseLng = formData.location.storeCoordinates.lng;
                                        fetchNearbyCoffeeShops(baseLat, baseLng, adminOverride
                                            ? { adminLat: adminOverride.lat, adminLng: adminOverride.lng }
                                            : undefined);
                                    }}
                                    lat={formData.location.storeCoordinates.lat}
                                    lng={formData.location.storeCoordinates.lng}
                                    city={formData.location.city}
                                    country={formData.location.country}
                                    clusterId={nearbyClusterId || undefined}
                                    initialShopName={shopListFilter}
                                />
                            </div>
                        )}

                        {isHomeMade && isVerifiedOwner && (
                            <HomeBrewFields
                                machines={myMachines}
                                selectedMachineId={selectedMachineId}
                                onSelectMachine={handleSelectMachine}
                                brew={homeBrew}
                                onBrewChange={handleBrewChange}
                                beans={myBeans}
                                capsules={myCapsules}
                                selectedBeanId={selectedBeanId}
                                selectedCapsuleId={selectedCapsuleId}
                                onSelectBean={setSelectedBeanId}
                                onSelectCapsule={setSelectedCapsuleId}
                            />
                        )}

                        <div className="space-y-3">
                            <header>
                                <h3 className="text-sm font-semibold text-gray-700 lowercase">upload coffee image</h3>
                            </header>

                            <ImageUploadSection
                                previewUrl={previewUrl}
                                isDragging={isDragging}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                                onClick={() => fileInputRef.current?.click()}
                                inputRef={fileInputRef}
                                onFileChange={handleImageSelect}
                            />
                        </div>
                          </>
                        )}
                    </section>
                )}

                {/* Step 2 (supermarket): Brand */}
                {currentStep === 2 && isSupermarket && (
                    <section className="space-y-4">
                        <header>
                            <h3 className="text-sm font-semibold text-gray-700 lowercase">brand</h3>
                        </header>
                        <BrandPicker value={brand} onChange={setBrand} disabled={isSubmitting} />
                    </section>
                )}

                {/* Step 2 (shop/home): Location + Coffee Shop */}
                {currentStep === 2 && !isSupermarket && (
                    <section className="space-y-4">
                        <header>
                            <h3 className="text-sm font-semibold text-gray-700 lowercase">location & coffee shop</h3>
                        </header>

                        <div className="space-y-3">
                            <label className="text-xs text-gray-500 flex items-center gap-2">
                                <span>coffee shop</span>
                                <span className="text-pink">*</span>
                            </label>

                            {/* Location and Coffee Shop Controls - Hidden for home made coffee */}
                            {!isHomeMade && (
                                <LocationControls
                                    isHomeMade={isHomeMade}
                                    hasGpsLocation={hasGpsLocation}
                                    isGettingLocation={isGettingLocation}
                                    locationError={locationError}
                                    locationText={`${formData.location.city}, ${formData.location.country}`}
                                    onGetLocation={getCurrentLocation}
                                    coordinates={formData.location.storeCoordinates}
                                    isAdmin={canManualLocation}
                                    onManualOverride={handleManualOverride}
                                    onStoreCoordinatesCommit={handleStoreCoordinatesCommit}
                                />
                            )}

                            {!isHomeMade && canManualLocation && (
                                <Alert variant="success">
                                    <AlertDescription className="text-xs">
                                        🌍 <strong>Admin/Coffee-Master:</strong> Search radius unlimited
                                    </AlertDescription>
                                </Alert>
                            )}

                            {/* Home Made Coffee Option for Admin/Coffee-Master */}
                            {canAddHomeMade && (
                                <div className="p-4 border border-ink/10 bg-muted/20 rounded-xl">
                                    <label className="flex items-start gap-3 cursor-pointer">
                                        <Checkbox
                                            checked={isHomeMade}
                                            onCheckedChange={(checked) => handleHomeMadeChange(checked === true)}
                                            className="mt-0.5"
                                        />
                                        <div className="flex flex-col gap-1 flex-1">
                                            <span className="text-sm font-medium text-foreground">🏠 home made coffee</span>
                                            <span className="text-xs text-muted-foreground">
                                                (Admin/Coffee-Master only)
                                            </span>
                                        </div>
                                    </label>
                                </div>
                            )}

                            <div className="space-y-3">
                                {canManualLocation ? (
                                    /* Admin/Coffee-Master: Full search functionality with manual input */
                                    <Input
                                        type="text"
                                        name="coffeeShop"
                                        value={coffeeShopSearch || formData.coffeeShop}
                                        onChange={handleCoffeeShopChange}
                                        placeholder={isHomeMade ? "Home Made" : "Search coffee shops or type manually"}
                                        required={!isHomeMade}
                                        disabled={isHomeMade}
                                    />
                                ) : (
                                    /* Normal users: No manual input - only nearby coffee shops selection */
                                    !isHomeMade && (
                                        <div className="normal-user-coffee-selection">
                                            <h4 style={{ marginBottom: '15px', color: '#495057' }}>
                                                Choose a Coffee Shop
                                            </h4>
                                            {/* <p style={{ fontSize: '14px', color: '#6c757d', marginBottom: '15px' }}>
                                                            Select from nearby coffee shops or browse more options
                                                        </p> */}
                                        </div>
                                    )
                                )}

                                {/* Cancel button when a coffee shop is selected */}
                                {formData.coffeeShop && !isHomeMade && (
                                    <div className="flex items-center justify-between gap-3 p-3 border border-mint/30 bg-mint/5 rounded-lg">
                                        <span className="text-sm">
                                            Selected: <strong>{formData.coffeeShop}</strong>
                                        </span>
                                        <Button
                                            type="button"
                                            variant="destructive"
                                            size="sm"
                                            onClick={() => {
                                                setFormData(prev => ({
                                                    ...prev,
                                                    coffeeShop: '',
                                                    coffeeShopRef: undefined,
                                                    coffeeShopDetails: undefined
                                                }));
                                                setCoffeeShopSearch('');
                                            }}
                                        >
                                            ✕
                                        </Button>
                                    </div>
                                )}

                                {/* Coffee shop selection list - only show when no shop selected and not home made */}
                                {!isHomeMade && !formData.coffeeShop && (
                                    <>
                                        <CoffeeShopList
                                            shops={nearbyCoffeeShops}
                                            isLoading={isLoadingCoffeeShops}
                                            hasLocation={hasGpsLocation || formData.location.storeCoordinates.lat !== 0}
                                            selectedPlaceId={formData.coffeeShopRef || formData.coffeeShopDetails?.place_id}
                                            onSelect={handleCoffeeShopSelect}
                                            showAdminStats={canManualLocation}
                                            isAdmin={canManualLocation}
                                            maxShops={30}
                                            onAddNewShop={() => setShowAddShopDialog(true)}
                                            searchValue={shopListFilter}
                                            onSearchChange={setShopListFilter}
                                            onGoogleSearch={handleInlineGoogleSearch}
                                            googleResults={googleShopResults}
                                            isSearchingGoogle={isSearchingGoogleShops}
                                            googleSearched={googleShopsSearched}
                                            onSelectGoogleResult={handleSelectGoogleResult}
                                            proposingPlaceId={proposingPlaceId}
                                        />
                                        <AddCoffeeShopDialog
                                            open={showAddShopDialog}
                                            onClose={() => setShowAddShopDialog(false)}
                                            onShopAdded={(shop) => {
                                                handleCoffeeShopSelect(shop);
                                                setNearbyCoffeeShops(prev => [shop, ...prev]);
                                                fetchNearbyCoffeeShops(formData.location.storeCoordinates.lat, formData.location.storeCoordinates.lng);
                                            }}
                                            lat={formData.location.storeCoordinates.lat}
                                            lng={formData.location.storeCoordinates.lng}
                                            city={formData.location.city}
                                            country={formData.location.country}
                                            clusterId={nearbyClusterId || undefined}
                                            initialShopName={shopListFilter}
                                        />
                                    </>
                                )}
                            </div>

                            {/* service type selector - only show when not home made */}
                            {!isHomeMade && (
                                <div className="space-y-2 pt-2">
                                    <label className="text-xs text-gray-500">how did you get it?</label>
                                    <div className="flex gap-2">
                                        {([
                                            { value: 'dine-in' as const, label: 'Dine-in', icon: '🏪' },
                                            { value: 'takeaway' as const, label: 'Takeaway', icon: '🥤' },
                                            { value: 'delivery' as const, label: 'Delivery', icon: '🛵' },
                                        ]).map((option) => (
                                            <button
                                                key={option.value}
                                                type="button"
                                                onClick={() => setServiceType(prev => prev === option.value ? '' : option.value)}
                                                className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-xl text-sm font-medium transition-all border-2 ${
                                                    serviceType === option.value
                                                        ? 'border-mint bg-mint/10 text-foreground shadow-sm'
                                                        : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'
                                                }`}
                                            >
                                                <span className="text-base">{option.icon}</span>
                                                <span>{option.label}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </section>
                )}

                {/* Step 3 (supermarket): Type */}
                {currentStep === 3 && isSupermarket && (
                    <section className="space-y-4">
                        <header>
                            <h3 className="text-sm font-semibold text-gray-700 lowercase">
                                type {brand?.brandName && <span className="text-gray-400 normal-case font-normal">— {brand.brandName}</span>}
                            </h3>
                        </header>
                        <TypePicker
                            brandIdOrSlug={brand?.brandRef}
                            value={selectedType}
                            onChange={setSelectedType}
                            disabled={isSubmitting}
                        />
                    </section>
                )}

                {/* Step 3 (shop/home): Coffee Type + Format */}
                {currentStep === 3 && !isSupermarket && (
                    <section className="space-y-4">
                        <header>
                            <h3 className="text-sm font-semibold text-gray-700 lowercase">coffee type & format</h3>
                        </header>

                        <CoffeeTypeSelector
                            loadingCoffeeTypes={loadingCoffeeTypes}
                            coffeeTypes={coffeeTypes}
                            iconUrls={iconUrls}
                            selectedCoffeeType={formData.coffeeType}
                            onSelect={handleCoffeeTypeSelect}
                            coffeeTypesCache={coffeeTypesCache}
                            showOtherInput={showOtherInput}
                            coffeeTypeSelected={coffeeTypeSelected}
                            onChooseAgain={handleChooseAgainCoffee}
                        />

                        {/* Format Options Section */}
                        {formData.coffeeType && coffeeTypeSelected && coffeeTypesFetched && coffeeTypesCache[formData.coffeeType] && (
                            <FormatOptions
                                coffeeType={formData.coffeeType}
                                coffeeTypesCache={coffeeTypesCache}
                                format={formData.format}
                                setFormat={(newFormat) => setFormData((prev) => ({ ...prev, format: newFormat }))}
                                formatOptionsSelected={formatOptionsSelected}
                                setFormatOptionsSelected={setFormatOptionsSelected}
                                handleSugarChange={handleSugarChange}
                                handleNestedFormatChange={handleNestedFormatChange}
                                handleFormatChange={handleFormatChange}
                            />
                        )}
                    </section>
                )}

                {/* Step 4 (supermarket): Rating + Description — criteria derived from the
                    SELECTED TYPE's format (not the backend `categories`, so custom types work). */}
                {currentStep === 4 && isSupermarket && (
                    <section className="space-y-6">
                        <CoffeeRatingSection
                            mode={ratingMode}
                            onModeChange={setRatingMode}
                            coffeeType=""
                            coffeeTypesCache={coffeeTypesCache}
                            coffeeFormat={selectedType ? supermarketCoffeeFormat(selectedType.format) : { sugar: true }}
                            rating={formData.rating}
                            onRatingChange={handleRatingChange}
                            centeredSliderPositions={centeredSliderPositions}
                            onCenteredSliderChange={handleCenteredSliderChange}
                            quickRate={formData.quickRate}
                            onQuickRateChange={handleQuickRateChange}
                            extraCategories={
                                selectedType
                                    ? supermarketRateCategories(selectedType.format)
                                    : { taste: { weight: 3, required: true }, value: { weight: 2, required: false } }
                            }
                        />

                        {descriptionBlock}
                    </section>
                )}

                {/* Step 4 (shop/home): Rating + Description */}
                {currentStep === 4 && !isSupermarket && (
                    <section className="space-y-6">
                        <CoffeeRatingSection
                            mode={ratingMode}
                            onModeChange={setRatingMode}
                            coffeeType={formData.coffeeType}
                            coffeeTypesCache={coffeeTypesCache}
                            coffeeFormat={coffeeTypesCache[formData.coffeeType]?.coffeeFormat}
                            rating={formData.rating}
                            onRatingChange={handleRatingChange}
                            centeredSliderPositions={centeredSliderPositions}
                            onCenteredSliderChange={handleCenteredSliderChange}
                            quickRate={formData.quickRate}
                            onQuickRateChange={handleQuickRateChange}
                            centeredCategories={{
                                acidity: {
                                    leftLabel: 'very acidic',
                                    rightLabel: 'not acidic',
                                    maxRange: 5,
                                },
                                ...(isHotCoffeeType(formData.coffeeType) ? {
                                    temperature: {
                                        leftLabel: 'too cold',
                                        rightLabel: 'too hot',
                                        maxRange: 5,
                                    },
                                } : {}),
                            }}
                            extraCategories={isHotCoffeeType(formData.coffeeType) ? {
                                temperature: { weight: 0, required: false },
                            } : undefined}
                        />

                        {descriptionBlock}
                    </section>
                )}


                <button
                    type="submit"
                    className="submit-button"
                    disabled={isSubmitting || !isFormValid()}
                    style={{ display: 'none' }}
                >
                    {isSubmitting && <span className="loading-spinner"></span>}
                    {isSubmitting
                        ? statusInfo.message || 'Processing...'
                        : currentStep === 1 ? 'Next: Details'
                            : currentStep === 2 ? 'Next: Rating'
                                : 'Create Coffee Entry'
                    }
                </button>

                {/* Step bar rendered via MobileSheet footer prop on mobile, inline on desktop */}
                {!isMobile && (
                    <div className="sticky bottom-0 -mx-4 px-4 bg-gray-200/95 backdrop-blur-sm border-t border-gray-300/50 py-2 z-20">
                        {navInner}
                    </div>
                )}

                {/* Inline error display for failed submissions */}
                {(currentStatus === 'failed' || currentStatus === 'error') && errorMessage && (
                    <div className="mt-4">
                        <SubmissionError
                            error={errorMessage}
                            errorDetails={errorDetails}
                            failedFormStep={failedFormStep}
                            canRetry={canRetry}
                            retryCount={statusInfo.retryCount}
                            maxRetries={statusInfo.maxRetries}
                            onRetry={() => {
                                if (retrySubmission()) {
                                    setTimeout(() => {
                                        if (isSupermarket) {
                                            finalizeSupermarketEntry();
                                        } else {
                                            finalizeCoffeeEntry();
                                        }
                                    }, 500);
                                }
                            }}
                            onEdit={(step) => {
                                resetSubmission();
                                setCurrentStep(step);
                            }}
                            onDismiss={() => {
                                resetSubmission();
                            }}
                        />
                    </div>
                )}

                {/* On `completed`, the submission hook auto-dismisses after ~2s — no manual reset CTA needed */}
            </form>
        </>
    );

    return (
        <>
            {isMobile ? (
                <MobileSheet
                    open={true}
                    onOpenChange={(open) => !open && onClose()}
                    maxHeight="85vh"
                    fullModal={false}
                    disableDrag={true}
                    showGrabber={false}
                    showCloseButton={true}
                    overlayClosable={true}
                    title="add new coffee"
                    footer={
                        <div className="bg-gray-100 rounded-t-2xl px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom,0px))]">
                            {navInner}
                        </div>
                    }
                >
                    <div className="pb-4">
                        {formContent}
                    </div>
                </MobileSheet>
            ) : (
                <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
                    <DialogContent className="max-w-2xl">
                        <DialogHeader>
                            <DialogTitle>Add New Coffee</DialogTitle>
                        </DialogHeader>
                        {formContent}
                    </DialogContent>
                </Dialog>
            )}

            {/* Resume Draft Dialog */}
            <ResumeDraftDialog
                open={showResumeDialog}
                onResume={handleResumeDraft}
                onDiscard={handleDiscardDraft}
                draftInfo={draftInfo}
                draftAge={getDraftAge()}
            />
        </>
    );
};

export default AddCoffeeForm;